/**
 * Shared shadow-side CSS fragments - the app-chrome primitives a shadow-rooted component draws
 * that the global `public/style.css` also carries for the still-light pages.
 *
 * ## Why this file exists
 *
 * A shadow root sees the theme TOKENS (`--border`, `--text2`, …) because custom properties inherit
 * through the boundary, but NOT the global sheet's ordinary selector rules (`.hint { … }`,
 * `table { … }`). So a display component that draws a hint line, a table or a chip in shadow has to
 * carry those rules itself. The generic ones - a hint's muted small text, the plain table, a chip,
 * a status badge, the centered empty state - are wanted by more than one such component, and copying
 * them into each element's `static styles` by hand is how shadow copies drift apart. This is the one
 * place they live, imported as `static styles = [chromeStyles, css\`…own layout…\`]`.
 *
 * It is deliberately NOT unified with the global sheet: `public/style.css` keeps its own `.hint` /
 * `table` / `.chip` / `.badge` / `.empty-state` for every page still on light DOM (which is most of
 * them). The two copies converge only when the whole app is shadow and the global block dies - the
 * same lifecycle every migrated CSS block follows (see `components/ghElement.ts`). Until then, a
 * change to one of these primitives has to be made in both, on purpose.
 *
 * Each fragment is the UNION of the primitives its consumers collectively need, like a mini global
 * sheet: a given component uses the subset it draws and ignores the rest (the stats panel never
 * renders a `.chip`; the insights panel never renders a `.badge`), exactly as every page uses a
 * subset of `public/style.css`. As the shared-chrome components land, their fragments join this file:
 * `chromeStyles` with the two analytics panels (round 86), `buttonStyles` with `<gh-queue-composer>`
 * (round 87), `cardStyles`/`formStyles`/`modalStyles` with `<gh-campaign-controls>` (round 88), and
 * `controlStyles`/`spinnerStyles` with the FIRST page island to go shadow, `<gh-onboarding-page>`
 * (round 89). The last two are what a page island needs beyond a component: the form-control base box
 * (which does not inherit through the boundary) and a `.btn-busy` spinner whose `@keyframes` must be
 * copied into every shadow root that animates it. A round need not birth a fragment: round 91 (the
 * rejected/accepted list islands) minted none, only EXTENDING the union - `.badge-failed` into
 * `chromeStyles`, `.btn-ghost-danger` into `buttonStyles` - as those list pages became the first
 * shadow consumers to draw them. Round 92 (the Queue + Approved pitch islands) did the same:
 * `.badge-followup` into `chromeStyles` and `.btn-success` into `buttonStyles`. Round 95 (the
 * campaigns/sent/blogs list cohort) grew `chromeStyles` again - `.chip-danger`/`.chip-warning`, the
 * warning/danger chip variants blogs was the first shadow page to draw.
 *
 * Round-73 rule: an extracted module invisible to every consumer typechecks and lints while being
 * dead - so each fragment ships wired to a real consumer the round it is born (`chromeStyles` to
 * BOTH `<gh-stats-panel>` and `<gh-campaign-insights>`; `buttonStyles` to `<gh-queue-composer>`;
 * `cardStyles`/`formStyles`/`modalStyles` to `<gh-campaign-controls>`; `controlStyles` to BOTH
 * `<gh-onboarding-page>` and `<gh-queue-composer>` - which inlined that base in round 87 and adopts
 * the shared copy now - and `spinnerStyles` to `<gh-onboarding-page>`).
 */
import { css } from "lit";

/**
 * The global `* { margin:0; padding:0; box-sizing:border-box }` reset, which a shadow root LOSES - a
 * document-scope `*` rule does not style a shadow tree. Compose this FIRST in a page island's
 * `static styles`. Born wired to `<gh-queue-page>` + `<gh-approved-page>` (round 92).
 *
 * `box-sizing` was the half round 92 restored, because it had visibly broken something: every fragment
 * below (and the whole app's CSS) is written assuming `border-box`, so a `width: 100%` control plus its
 * padding and border overflowed its container by ~26px, and the Queue/Approved pitch editor's Subject
 * input and Body textarea rendered ~5px PAST the card's right edge until it landed.
 *
 * ⚠ **The MARGIN half was deliberately left out, and that was wrong - the owner reported it as "a lot
 * of vertical padding" on the Settings cards (2026-08-03).** The reasoning was that each fragment sets
 * the spacing it wants and can rely on the UA default elsewhere. But the UA default is not what this
 * app's CSS was written against: `public/style.css` zeroes every margin on `*`, so a rule like
 * `h3 { font-size: 1.125rem; margin-bottom: 10px }` means a top margin of ZERO on a light page and `1em`
 * on a shadow one - and that exact rule is copied verbatim into thirteen page islands, each of which
 * had been quietly carrying an extra ~18px above every heading and around every `<p>`. Inside a 20px
 * card that reads as a card with 38px of padding, which is what it looked like.
 *
 * ⚠ **The general form is the one that matters, because the next ambient thing will do it again: a
 * shadow twin of a global rule is only a twin if what the ORIGINAL sits on top of comes with it.** The
 * thirteen copies of the `h3` rule were faithful character-for-character and still rendered
 * differently, because the reset they were authored against stayed on the other side of the boundary.
 * When you copy a rule into a shadow root, copy what it inherited too - or it is a different rule
 * wearing the same text.
 *
 * Restoring the margins can only ever REMOVE space here (every fragment and page rule below composes
 * after this one and overrides it), so the failure direction is "something the UA was padding for us
 * is now flush", which is visible, rather than a silent drift.
 *
 * ⚠ **That last paragraph was wrong, and it is the reason to read this one** (2026-08-04, the owner
 * reported the dashboard's KPI tiles with their text flush against the card's left edge). "Everything
 * below composes after this one and overrides it" is true of the rules in the SAME shadow root, and
 * this `*` does not stop at that root: it matches the HOST of every component the island renders
 * inside itself, and there it wins - **for normal declarations the cascade prefers the OUTER tree
 * over the inner one, regardless of specificity or source order.** That is the reverse of every other
 * cascade intuition, and it makes `:host` the one rule a component writes that its container can
 * overrule. Three components were declaring their box spacing there: `<gh-stat-tile>`'s
 * `padding: 10px 12px` (since round 83), and `<gh-bar-rows>`/`<gh-funnel-bars>`' `margin-bottom`.
 * All three went to zero the day this fragment grew its margin/padding half, in every island at once.
 *
 * ⚠ So the safe-failure-direction claim above holds only for what the ISLAND itself draws. Reaching a
 * nested component's host, the same rule is silent rather than visible: the component's CSS is
 * unchanged and correct read alone, this fragment is unchanged and correct read alone, and neither
 * file mentions the other. `tests/theme.test.ts` now fails any `:host` block in `src/frontend` that
 * declares padding or margin, with the instruction to move it inside the shadow root - that gate, not
 * this comment, is what stops the next component walking into it.
 */
export const resetStyles = css`
	*,
	*::before,
	*::after {
		margin: 0;
		padding: 0;
		box-sizing: border-box;
	}
`;

/**
 * The focus ring (§R, WCAG 2.4.7) - the shadow-side twin of `public/style.css`'s rule.
 *
 * ⚠ **Before §R this app had TWO `:focus-visible` rules in total**, which is not one accessibility
 * bug among several: a keyboard user could not see where they were, so every OTHER keyboard fix in
 * the app was unverifiable by a human. That is why this fragment is composed into every island and
 * component rather than added where someone noticed a gap.
 *
 * ⚠ **It has to be a fragment and cannot be one rule in the global sheet**, for the reason every
 * ambient condition in this app has taught (round 148, and the motion tokens' own comment): selector
 * CSS stops at a shadow boundary and every island and component has been shadow since round 100. So
 * a `button:focus-visible` written once at document scope draws a ring on the sidebar and on
 * absolutely nothing else. What crosses is the custom properties, which is why the VALUES live at
 * `:root` (`--focus-ring-*`) and only the RULE is duplicated - the two copies cannot drift on the
 * thing a reader actually sees.
 *
 * ⚠ **`:host(:focus-visible)` is a separate case from `:focus-visible`, and both are needed.** An
 * element that delegates focus to something inside itself matches on its HOST; one that renders an
 * ordinary `<button>` matches inside its own root. Writing only the second leaves `<gh-select>` and
 * `<gh-multiselect>` - the two controls a keyboard user meets most - with no ring at all.
 *
 * ⚠ **`outline: none` on a `:focus` rule is what this fragment exists to replace, and it is never
 * the fix for an ugly ring.** Five card rosters and two controls carried one (`.release-card`,
 * `.artist-card`, `.campaign-card`, `.mission-card`, and the two shadow `<select>`/search inputs),
 * each of them substituting a border-color change - the SAME color the hover state uses, so a
 * keyboard user could not tell "I am here" from "the mouse is over there". If a ring lands wrong,
 * move it with `outline-offset` or a radius; do not turn it off.
 *
 * Born wired to every island and component in the app, which is the only consumer set that makes
 * sense for it - `tests/a11y.test.ts` fails a `static styles` in `src/frontend` that omits it.
 */
export const focusStyles = css`
	:host(:focus-visible),
	a:focus-visible,
	button:focus-visible,
	input:focus-visible,
	select:focus-visible,
	textarea:focus-visible,
	summary:focus-visible,
	[tabindex]:focus-visible {
		outline: var(--focus-ring-width) solid var(--focus-ring-color);
		outline-offset: var(--focus-ring-offset);
		border-radius: var(--radius);
	}
`;

/** Generic app-chrome primitives for a shadow-rooted display component (hint, table, chip, badge, empty state, h4). */
export const chromeStyles = css`
	/* Explanatory copy under a section or empty state - muted and smaller than body text */
	.hint {
		font-size: 0.8125rem;
		line-height: 1.5;
		color: var(--text2);
	}
	/* ⚠ A HEADING THAT CARRIES AN ICON IS A ROW, not a text run with a picture in it (owner,
	   2026-08-11, of the Content page: *"notice how the icon isn't exactly vertically aligned with the
	   title next to it"* - and they added, correctly, that it was every one of them). An inline icon
	   hangs off the BASELINE, which is the right anchor inside a sentence and the wrong one beside a
	   heading: the box is 1em tall while the capitals it sits next to are about 0.72em, so it rides
	   high by the difference. This is the same call the buttons already made - a button is a flex row
	   for exactly this reason - arriving at the other place the app puts a glyph beside words.
	   ⚠ It is scoped with :has(svg) rather than applied to every heading, because a heading with no
	   icon has nothing to align and flex would quietly change how a centered or truncated one lays
	   out. The gap replaces the markup space, which is a word space wide and reads as no gap at all
	   against a glyph filling its own box (buttonStyles' note, same finding). */
	h2:has(svg),
	h3:has(svg),
	h4:has(svg) {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
	}
	/* THE PAGE TITLE (frontend/pageHeading.ts), drawn HERE rather than in each island. Round 306 gave
	   the page h2 its display face by copying one rule into sixteen shadow roots, and the pages that
	   came to the pattern later - Posts, Insights - never got a copy, so their titles set in the reading
	   face beside every other page's (2026-09-23, only a screenshot showed it). A class the helper
	   always writes, in a fragment every page already composes, is a copy nobody can forget. */
	.page-heading {
		font-family: var(--font-display);
		font-stretch: var(--font-display-stretch);
		font-weight: 800;
		font-size: 1.75rem;
		line-height: 1.15;
		margin-bottom: 6px;
	}
	/* Two or three views of one list as one switch (§AL, 2026-09-23 - Sent and Outlets): the open one
	   is ink, like the queue's open row. It replaced a pair of small buttons whose "current" state was
	   DISABLED, which read as unavailable rather than as chosen. Buttons carry aria-pressed. */
	.view-switch {
		display: inline-flex;
		gap: 2px;
		padding: 3px;
		margin-bottom: 16px;
		border: 1px solid var(--border);
		border-radius: 999px;
		background: var(--bg2);
		/* Four views do not fit a phone (Compose's tiers, round 325): the pill scrolls sideways inside
		   itself rather than pushing the page wider or folding a label onto two lines */
		max-width: 100%;
		overflow-x: auto;
		scrollbar-width: none;
	}
	.view-switch button {
		flex: none;
		white-space: nowrap;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 14px;
		border: 0;
		border-radius: 999px;
		background: none;
		font: inherit;
		font-size: 0.8125rem;
		font-weight: 600;
		color: var(--text2);
		cursor: pointer;
	}
	.view-switch button:hover {
		color: var(--text);
	}
	.view-switch button[aria-pressed="true"] {
		background: var(--chrome-bg);
		color: var(--chrome-text);
	}
	/* Section headings - the release-workspace spacing (18px), now consistent wherever a panel is
	   shown: it used to inherit .release-subpage h4 in the release tabs and the UA default on the
	   Artists bio tab, and in shadow it renders the same everywhere */
	h4 {
		margin: 18px 0 8px;
	}
	/* ⚠ **A heading's TOP margin is separation from what came BEFORE it, so it is wrong when nothing
	   did** (owner, 2026-08-10: "weirdly a lot of vertical padding over the headlines" on the Campaign
	   tab's two cards, whose h4 is the card's first child - 14px of card padding plus this 18px reads
	   as a 32px-padded card). It is the paragraph rule two blocks down in its other direction, and the
	   same finding the h3 restore made in 2026-08-03: **spacing between siblings must not become
	   padding at an edge.** One line here fixes every card in the app that opens with a heading.
	   (No backticks in a css comment - one of them closes the tagged template.) */
	h4:first-child {
		margin-top: 0;
	}
	/* The KPI grid. It became a shared primitive in round 184, when a THIRD shadow root needed it
	   (the Artists page's Audience tab) and copying five lines a third time stopped being defensible.
	   ⚠ It is still duplicated in the global stylesheet and that copy STAYS: light-DOM callers draw
	   it from there and a fragment cannot reach them. Rounds 86 and 94's notes in statsPanel.ts and
	   dashboard.ts recorded that duplication honestly; this is the half of it a fragment CAN own.
	   ⚠ No backticks in here - one closes the tagged template (the round-86 gotcha). */
	.stat-tiles {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
		gap: 10px;
		margin-bottom: 14px;
	}
	table {
		width: 100%;
		border-collapse: collapse;
		font-size: 0.875rem;
	}
	/* ⚠ **THE MINIMAL SCROLLER FOR A TABLE INSIDE A COMPONENT** (round 258). tableStyles' .table-wrap
	   is the PAGE-level one and carries a surface and a top margin with it, which is right on a page
	   whose main content is a table and wrong inside a card that already has both. This is the same
	   overflow rule with nothing else attached, so a component can stop a wide table widening the
	   whole document without changing how the card looks at any width it already fits.
	   ⚠ It is here in chromeStyles rather than copied into four shadow roots because that is the
	   fragment all four already compose - a hand-copied overflow rule is how three of them keep it
	   and the fourth loses it. Four tables in this app had NO scroller of any kind: the stats panel's
	   recipients, the campaign angles, the outlet duplicate card and the Shorts lab. */
	.table-scroll {
		overflow-x: auto;
	}
	th,
	td {
		padding: 10px 12px;
		text-align: left;
		border-bottom: 1px solid var(--border);
	}
	th {
		color: var(--text2);
		font-weight: 600;
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}
	/* Chips: small labels/annotations, subtler than badges */
	.chip {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		font-size: 0.6875rem;
		line-height: 1.4;
		padding: 2px 8px;
		border-radius: 999px;
		border: 1px solid var(--border);
		background: var(--bg3);
		color: var(--text2);
		white-space: nowrap;
	}
	/* A hit goal / a dispatched wave - the affirmative chip variant */
	.chip-success {
		color: var(--success);
		border-color: var(--success);
		background: transparent;
	}
	/* An accented annotation (a score star, a live count) - joined with the dashboard island (round 94) */
	.chip-accent {
		color: var(--accent-text);
		border-color: var(--accent);
		background: transparent;
	}
	/* Warning/danger chip variants - joined with the blogs island (round 95), the first shadow
	   consumer to draw them (a stale outlet, a bounced email). They STAY global too for the
	   still-light shortsStudio/runway/compose pages that also draw them. */
	.chip-warning {
		color: var(--warning);
		border-color: var(--warning);
		background: transparent;
	}
	.chip-danger {
		color: var(--danger);
		border-color: var(--danger);
		background: transparent;
	}
	.badge {
		display: inline-block;
		font-size: 0.6875rem;
		padding: 3px 8px;
		border-radius: 12px;
		font-weight: 600;
		text-transform: uppercase;
		white-space: nowrap;
	}
	.badge-approved {
		background: var(--success);
		color: var(--on-success);
	}
	.badge-queued {
		background: var(--warning);
		color: var(--on-warning);
	}
	.badge-failed {
		background: var(--danger);
		color: var(--on-danger);
	}
	/* The follow-up marker both pitch pages put in front of the outlet name (via pitchCard.ts).
	   A fixed blue with dark text, not a theme token - it reads the same on both palettes. Shared
	   with the still-light settings page, so it STAYS global too; this is the shadow-side twin. */
	.badge-followup {
		background: #74b9ff;
		color: #333;
	}
	/* The shadow twin of the global rule - see frontend/emptyState.ts for why an empty state draws an
	   icon at all. Both copies change together, on purpose (this file's header).
	   (No backticks in here: one inside a css template closes it, CSS comment or not.) */
	/* ⚠ Kept character-identical to the empty-state block in public/style.css - see the long note
	   there for why it is a REGION rather than a floating sentence (2026-08-03). */
	.empty-state {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: center;
		gap: 18px;
		text-align: left;
		padding: 24px;
		color: var(--text2);
		background: color-mix(in srgb, var(--bg2) 55%, transparent);
		border: 1px dashed var(--border);
		border-radius: var(--radius);
	}
	/* The message column of an empty state: the sentence, then the ONE thing to do about it on its own
	   line. A link spliced into the sentence wraps mid-phrase at whatever width the card happens to be
	   - see the emptyState helper for the report that produced this. */
	.empty-text {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 6px;
		min-width: 0;
	}
	/* The sentence keeps its own inline flow - a trailing ? belongs beside the words, not under
	   them, and the column around it would otherwise make each a flex item of its own. */
	.empty-message {
		display: inline;
	}
	.empty-action {
		font-size: 0.8125rem;
		font-weight: 600;
	}
	/* ⚠ A paragraph's spacing has to be OURS. Both resets (the global sheet's and resetStyles') zero
	   every margin on *, so a <p> inherits nothing from the UA - and from the day resetStyles grew its
	   margin half, every paragraph in every island sat flush against whatever followed it. The owner
	   reported it as a button with no gap above it on the Settings data cards (2026-08-04); it was
	   every <p> in the app. Bottom only, so a paragraph never pushes down on the heading above it -
	   that top margin is exactly what made cards look 38px-padded before the reset landed. */
	p {
		margin-bottom: 12px;
	}
	p:last-child {
		margin-bottom: 0;
	}
	/* ⚠ **AN EMPTY STATE'S FRAME NEVER MOVES. ONLY ITS CONTENTS DO** (owner, 2026-08-12: *"never make
	   an empty state illustration sway. only animate its inner contents"*). This element carried a
	   whole-picture rise for a day and a half, and it is gone rather than quietened.

	   The rule is worth more than the deletion, because the rise was the CHEAP answer to "make the
	   drawings move" - one rule reaching every drawing, including the ones nobody had drawn yet - and
	   cheap is exactly what was wrong with it. **A picture that floats says nothing about what it is a
	   picture OF.** It is the landing page's rule (only animate what the subject actually does) applied
	   one level up: a drawing sitting in a card is a thing sitting still, and a card that drifts beside
	   a table reads as a rendering fault rather than as life. Everything that moves in this family now
	   means something - the record turns, the needle hunts, the envelope lifts - and a drawing with no
	   part worth moving is simply still, which is a fine thing for a picture to be.

	   ⚠ **So a new drawing does NOT inherit motion any more**, which is the cost of this rule and the
	   thing to remember: it moves when somebody gives one of its parts a reason to. */
	.empty-art {
		width: 96px;
		max-width: 40%;
		height: auto;
		display: block;
	}
	/* ⚠ THE DRAWING DOES WHAT THE THING DOES (owner ask, 2026-08-12: *"animate more of your
	   illustrations, such as the empty states - for example the one on the Sent page with the letter
	   thingy"*, then *"move relevant things inside them in cute and semantically relevant ways"*). The
	   breathe above moves every drawing as a whole; these move ONE part of nine of them, and the test
	   for whether a part earns motion is the landing page's rule from round 249's session: **only
	   animate what the subject actually does.** A record turns, a plane flies, a compass needle hunts
	   for north, a sparkle sparkles, a spark jumps a gap, sound leaves a mic and a megaphone, a
	   waveform rides the song, a burst bursts.

	   ⚠ **The motion belongs to the DRAWING, not to the frame it hangs in** - which is why these
	   selectors carry no container (they hung off the empty-state container for a day). The same pictures render
	   in three places: an empty state, the Artists/Releases start card (its own class, cropped to the
	   sheet) and the Quick Start step heads. A record that turns on one page and stands still on
	   another is not a quieter version of the feature, it reads as the second page being broken. Every
	   one of those hosts composes this fragment, and every animation below is fill-box-relative, so
	   the crop cannot move an origin.

	   ⚠ **What is deliberately still, because motion has to be REVERSIBLE and some real actions are
	   not.** A tick landing in the approval queue's empty checkbox, a bar growing on the visits chart,
	   a pitch arriving in the inbox: all true things, all ONE-WAY, and a loop that must return to where
	   it started would play each of them backwards half the time - a box un-ticking, a chart draining.
	   Oscillations (sway, pulse, flicker, dip) and whole revolutions are the shapes that survive that
	   rule, which is most of why this set looks the way it does. The lens on the search drawing is
	   still for a second reason worth keeping: that drawing was composed so it would NOT say *the app
	   is still looking*, and a lens that sweeps says exactly that.

	   ⚠ **THE AMPLITUDES WERE ALL DOUBLED AN HOUR AFTER THEY SHIPPED** (owner, 2026-08-12, having sent
	   three screenshots of drawings that DO move: *"the animations are so subtle I can barely see any
	   movement"*). Every number here was chosen to be unobtrusive beside live content, and the sum of
	   those choices was motion nobody perceives - three units of travel over ten seconds is a still
	   picture with a cost. **The finding is that an amplitude is not a taste settled in the source: it
	   is a distance in real pixels at the size the thing renders, and the only way to know it is to
	   measure it.** A scratch harness now samples each part's box across a whole period at 96px; the
	   floor that came out of it is roughly **4px of excursion over 3-7 seconds**, and everything below
	   that had to be raised. Periods moved from 5-13s to 3-7s in the same pass, because a slow enough
	   motion is invisible at any amplitude - the eye needs to catch it inside a glance.
	   ⚠ **And put the motion on a part the eye is ALREADY ON.** The megaphone pulsed its two sound
	   arcs, which are the quiet text color at 45% - the faintest ink in that drawing - so all of its
	   motion was hiding in the part hardest to see. The cone rocks now, and that one drawing went from
	   *reported as static* to obvious without either number changing much.

	   ⚠ Three mechanics, all three of which are silent when wrong:
	   - **transform-box: fill-box on every animated CHILD.** Without it the transform origin is the
	     whole viewBox, so a two-degree tilt swings the part across the scene.
	   - **Every set returns to where it started.** Reduced motion collapses the period to 0.01ms and
	     the count to 1, so a set ending anywhere else parks the drawing in a displaced pose - a still
	     picture that is subtly wrong, which is worse than the animation it replaced.
	   - **Tokens, never literals.** A duration written as a number is one that reduced motion cannot
	     reach, and tests/theme.test.ts is what makes that a gate rather than a habit. */
	.art-move {
		transform-box: fill-box;
		transform-origin: center;
	}
	/* ⚠ An ORIGIN modifier, not a verb - it changes where a transform pivots and animates nothing on
	   its own. Anything standing ON something (a chart's bars, a board's columns) has to grow from its
	   baseline: scaled about its own centre a bar sinks through the axis it is drawn on, which reads as
	   a rendering fault rather than as a number changing. It is declared after the move rule on purpose
	   - same specificity, so source order is what lets a wearer of both pivot at its foot. */
	.art-base {
		transform-origin: bottom;
	}
	/* Copy being set: a line of blocked-out text grows from its left edge, the way words arrive on a
	   page. ⚠ It carries its own origin rather than composing the move rule, because a line growing
	   from its middle is a line that also grows LEFTWARDS - which no text does in this app's reading
	   direction, and reads as a highlight rather than as writing. */
	.art-type {
		transform-box: fill-box;
		transform-origin: left;
		animation: empty-art-type calc(var(--motion-sheen-loop) * 0.6) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-type {
		0%,
		100% {
			transform: scaleX(1);
		}
		45% {
			transform: scaleX(0.55);
		}
	}
	/* Riffling a stack - the picked card fans out and drops back. Small, because a card that swings is
	   a card that has come loose from the pile it belongs to. */
	.art-riffle {
		animation: empty-art-riffle calc(var(--motion-sheen-loop) * 0.65) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-riffle {
		0%,
		100% {
			transform: rotate(0deg);
		}
		35% {
			transform: rotate(-7.5deg);
		}
		70% {
			transform: rotate(5deg);
		}
	}
	/* A person, alive rather than posed - the smallest travel in the set on purpose. ⚠ Two figures nod
	   on DIFFERENT phases or they are one object with two heads; that is the delay classes' whole job. */
	.art-nod {
		animation: empty-art-nod calc(var(--motion-sheen-loop) * 0.55) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-nod {
		0%,
		100% {
			transform: translateY(0);
		}
		50% {
			transform: translateY(-6px);
		}
	}
	/* The plane the Sent page draws - it flies, so it drifts along its own line and settles back */
	.art-fly {
		animation: empty-art-fly calc(var(--motion-sheen-loop) * 0.75) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-fly {
		0%,
		100% {
			transform: translate(0, 0);
		}
		50% {
			transform: translate(9px, -9px);
		}
	}
	/* ⚠ **THE RECORD TURNS, and what it took is the whole lesson of this block.** The day before, this
	   comment said it could not be had from here - "the fill-box origin gives every element its OWN
	   centre, so the groove arc would orbit itself and fly off the record". Both halves of that were
	   true and the conclusion was not, because a fill-box origin is a fact about the GROUP you draw
	   around the moving parts: wrap the five concentric circles TOGETHER with the mark that has to
	   travel and the union bounding box is the outer circle's, whose centre is the record's centre by
	   construction. **Group the moving part with the symmetric thing it orbits and the origin comes out
	   right for free** - no view-box origin, no coordinates repeated in a stylesheet, and (the half
	   that decided it) nothing that breaks when a host crops the viewBox, which the start cards do.
	   ⚠ The sleeve's opening arc is NOT in that group, and it is the trap: it looks like a groove in the
	   source and it is the record's hidden half showing inside the sleeve. Rotating it would swing the
	   sleeve's own edge across the drawing, and it would have dragged the group's bbox two units left,
	   putting every rotation slightly off centre - one element in the wrong group, two failures.
	   ⚠ A full revolution is the only honest loop for a disc (the onboarding rings' rule) and it is
	   also the only one reduced motion can collapse: 360deg IS identity, so the single pass lands the
	   record exactly where it was drawn. Slow - a turntable at 33rpm beside a working page is a strobe,
	   and what has to read here is *this is a record*, not the RPM. */
	.art-spin {
		animation: empty-art-spin calc(var(--motion-sheen-loop) * 1.2) linear var(--motion-decor-iterations);
	}
	@keyframes empty-art-spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
	/* Sound leaving a mic or a megaphone: the arcs swell and fade, never move */
	.art-pulse {
		animation: empty-art-pulse calc(var(--motion-sheen-loop) * 0.5) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-pulse {
		0%,
		100% {
			opacity: 0.35;
			transform: scale(1);
		}
		50% {
			opacity: 0.95;
			transform: scale(1.34);
		}
	}
	/* ⚠ The megaphone's CONE rocks, and the reason is the owner's report: the arcs beside it are the
	   quiet text color at 45% - the faintest ink in the drawing - so pulsing them alone put every bit of
	   this picture's motion on the part hardest to see. **Animate a part the eye is already on.** A
	   megaphone is held by somebody, so a small rock is what it does; the cone and its handle rock
	   together as a group, or the handle stays behind and the megaphone comes apart. */
	.art-shout {
		animation: empty-art-shout calc(var(--motion-sheen-loop) * 0.5) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-shout {
		0%,
		100% {
			transform: rotate(0deg);
		}
		35% {
			transform: rotate(-7deg);
		}
		70% {
			transform: rotate(4.5deg);
		}
	}
	/* A burst bursts - the rays reach out and come back */
	.art-burst {
		animation: empty-art-burst calc(var(--motion-sheen-loop) * 0.5) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-burst {
		0%,
		100% {
			transform: scale(1);
			opacity: 0.5;
		}
		50% {
			transform: scale(1.28);
			opacity: 1;
		}
	}
	/* The four accent bars of the hook the app FOUND - they ride the song, so they scale about their
	   own middles. ⚠ That is a property of the drawing rather than a choice: all four bars are composed
	   centred on the same axis (y=46), which is what a symmetric waveform is, so a centre origin grows
	   them the way the song does. A baseline origin would turn the same drawing into a bar chart. */
	.art-bob {
		animation: empty-art-bob calc(var(--motion-sheen-loop) * 0.35) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-bob {
		0%,
		100% {
			transform: scaleY(1);
		}
		50% {
			transform: scaleY(0.45);
		}
	}
	/* A compass needle hunts for north and never quite settles - which is also what the card it sits in
	   is about. Small: at 96px this needle is 19px long, and past about ten degrees it stops reading as
	   a needle wavering and starts reading as a picture that is loose in its frame. */
	.art-swing {
		animation: empty-art-swing calc(var(--motion-sheen-loop) * 0.6) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-swing {
		0%,
		100% {
			transform: rotate(0deg);
		}
		30% {
			transform: rotate(-15deg);
		}
		65% {
			transform: rotate(11deg);
		}
	}
	/* A sparkle sparkles. Opacity AND scale together, because either alone reads as a bug: a shape that
	   only fades looks like it failed to load, and one that only grows looks like it is being hovered. */
	.art-twinkle {
		animation: empty-art-twinkle calc(var(--motion-sheen-loop) * 0.5) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-twinkle {
		0%,
		100% {
			transform: scale(1);
			opacity: 0.9;
		}
		45% {
			transform: scale(0.55);
			opacity: 0.35;
		}
		70% {
			transform: scale(1.3);
			opacity: 1;
		}
	}
	/* The spark in the gap between the plug and the socket. ⚠ The drawing is composed APART on purpose
	   - the step it illustrates is the one where nothing is connected - so the thing that moves is the
	   reach, never the plug: a plug sliding home would picture the finished state, which is the one
	   thing that drawing exists not to say. Electricity is the one subject in this set that is allowed
	   to be abrupt, so this one steps rather than eases. */
	.art-spark {
		animation: empty-art-spark calc(var(--motion-sheen-loop) * 0.55) ease-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-spark {
		0%,
		100% {
			opacity: 0.8;
			transform: scale(1);
		}
		40% {
			opacity: 0.2;
			transform: scale(0.75);
		}
		52% {
			opacity: 1;
			transform: scale(1.35);
		}
		64% {
			opacity: 0.4;
			transform: scale(0.9);
		}
	}
	/* The envelope on top of the pile the Compose queue draws: it lifts off the stack, because that is
	   the one thing a pitch waiting to go out is about to do. It comes back down, which is the honest
	   half - see the note above about one-way actions. */
	.art-lift {
		animation: empty-art-lift calc(var(--motion-sheen-loop) * 0.7) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-lift {
		0%,
		100% {
			transform: translateY(0);
		}
		50% {
			transform: translateY(-8px);
		}
	}
	/* A reply arriving: the second, smaller bubble leans in. Scale only, and gently - a bubble that
	   pops all the way in and out is a notification, and this card is a LIST of mentions rather than
	   one landing right now. */
	.art-pop {
		animation: empty-art-pop calc(var(--motion-sheen-loop) * 0.55) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-pop {
		0%,
		100% {
			transform: scale(1);
		}
		50% {
			transform: scale(1.22);
		}
	}
	/* The pencil on the Pitch Studio's page presses down and lifts, ALONG ITS OWN AXIS - the drawing is
	   at 45 degrees, so the travel is equal parts x and y or the pencil skates sideways off its point.
	   A pencil moving ACROSS the page was the obvious version and it is wrong twice: it drags the tip
	   off the sheet the drawing composes it on, and the strokes it would be leaving are already drawn. */
	.art-write {
		animation: empty-art-write calc(var(--motion-sheen-loop) * 0.45) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-write {
		0%,
		100% {
			transform: translate(0, 0);
		}
		50% {
			transform: translate(-6px, 6px) rotate(-4deg);
		}
	}
	/* The camera's indicator, blinking. It is the smallest motion in the set (a 2.5-unit dot, under 2px
	   where an empty state renders) and it survives that on opacity: a change of brightness reads at
	   any size, where a change of position does not. */
	.art-blink {
		animation: empty-art-blink calc(var(--motion-sheen-loop) * 0.45) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-blink {
		0%,
		100% {
			opacity: 0.7;
			transform: scale(1);
		}
		50% {
			opacity: 1;
			transform: scale(2.3);
		}
	}
	/* A twinkle for a part whose resting opacity is NOT the twinkle's - a swatch at 45%, a checkbox at
	   full. ⚠ It is scale-only for exactly that reason: an opacity keyframe is an absolute claim about
	   an element's rest, so the alternative was a class per resting value. The range is wider than the
	   other scales because its wearers are the smallest shapes in the set - 22% of an 11-unit box is
	   under two real pixels, which is the invisibility this whole family was just re-tuned out of. */
	.art-blip {
		animation: empty-art-blip calc(var(--motion-sheen-loop) * 0.5) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-blip {
		0%,
		100% {
			transform: scale(1);
		}
		45% {
			transform: scale(0.72);
		}
		72% {
			transform: scale(1.3);
		}
	}
	/* Something travelling ALONG a line - the two feeds folding into one reading on the crosspost
	   drawing. ⚠ Opacity only, and that is geometry rather than taste: these are curves whose ends are
	   anchored to the tiles at both sides, so any scale detaches them from what they connect. Both
	   wearers are drawn at 55%, which is what this set's 0% frame has to name. */
	.art-flow {
		animation: empty-art-flow calc(var(--motion-sheen-loop) * 0.6) ease-in-out var(--motion-decor-iterations);
	}
	@keyframes empty-art-flow {
		0%,
		100% {
			opacity: 0.55;
		}
		50% {
			opacity: 1;
		}
	}
	/* ⚠ Sibling parts of ONE drawing need DIFFERENT phases or they move as a sheet, which is the
	   onboarding hero's own rule (four cards at one period read as a single sliding plane rather than
	   as separate things floating). The offsets are NEGATIVE so nothing waits for its turn on first
	   paint - a drawing whose parts arrive one by one reads as a page still loading. */
	.art-delay-1 {
		animation-delay: calc(var(--motion-sheen-loop) * -0.13);
	}
	.art-delay-2 {
		animation-delay: calc(var(--motion-sheen-loop) * -0.26);
	}
	.art-delay-3 {
		animation-delay: calc(var(--motion-sheen-loop) * -0.39);
	}
	.empty-state gh-icon {
		display: grid;
		place-items: center;
		width: 54px;
		height: 54px;
		border-radius: 50%;
		background: color-mix(in srgb, var(--text2) 9%, transparent);
		color: color-mix(in srgb, var(--text2) 80%, transparent);
	}
	/* ⚠ An in-app link inside an empty state's SENTENCE is underlined, and axe caught the day it was
	   not (2026-08-10, when the Content page's four unconnected panels became illustrated cards). A
	   link inside a text block distinguished only by colour fails WCAG 1.4.1 - the rest of the app's
	   links get away with colour alone because they sit in chrome, where nothing around them is a
	   competing colour. This is the one place a link is embedded in prose. */
	.empty-state a {
		color: var(--accent-text);
		text-decoration: underline;
		text-underline-offset: 2px;
	}
	.empty-state span {
		max-width: 46ch;
		line-height: 1.55;
		/* Matches .hint, which is what the rest of a page's explanatory copy is set in. It was
		   inheriting the body size, so an empty state read LARGER than the live content it stands
		   in for - the one place on a page that should be the quietest. */
		font-size: 0.8125rem;
	}
`;

/**
 * The button family a shadow-rooted component draws - the shadow-side twin of the global `.btn`
 * block in `public/style.css`. Same lifecycle and same NOT-unified rationale as `chromeStyles`:
 * the global block stays for every light page, this is the copy a shadow root can see, and the two
 * converge only when the whole app is shadow.
 *
 * The UNION its shadow consumers draw - `<gh-queue-composer>` (`.btn`/`.btn-primary`/`.btn-small`/
 * `.btn-row`, round 87), `<gh-campaign-controls>` (which added `.btn-danger` + the `.queue-actions`
 * action-row, round 88), and the rejected/accepted list islands (which added `.btn-ghost-danger`,
 * the per-row delete button, round 91), and the Queue + Approved pitch islands (which added
 * `.btn-success`, the Approve / Send-all action, round 92 - it had waited here, absent, for exactly
 * this: the round its consumer flips, the way the badges joined `chromeStyles`, never speculatively).
 * No `.btn + .btn { margin-left }` rule: every shadow consumer's
 * buttons sit in a flex row (`.btn-row`/`.queue-actions`/`.modal-actions`/`.table-actions`) where `gap`
 * spaces them, so the sibling margin - and its per-container resets - are moot. The `--*` theme tokens
 * inherit through the boundary, so the buttons color automatically.
 */
export const buttonStyles = css`
	/* ⚠ A qualifier INSIDE a button keeps the button's own color (owner, 2026-08-04: "this looks
	   particularly bad and almost illegible"). The hint primitive is muted gray against the page,
	   which is right everywhere except here: on a filled primary button that gray lands on the accent
	   fill and the words all but disappear.

	   ⚠ **The an opacity of 0.75 that used to ride along with it is GONE (2026-08-25), and the argument
	   for it is the part worth reading.** It said: dimming by opacity works on any fill, which naming
	   a colour cannot - a second colour would be right for one variant and wrong for the next. That
	   was true and still is; what it missed is that the multiplier lands on a pairing this app has
	   already measured. --on-accent on --accent is gated at 4.5:1 BY NAME in tests/theme.test.ts,
	   and 0.75 of it is **3.47:1** - under AA in all four palettes, including the two that exist
	   precisely for readers who need more. ⚠ **A gate that measures a TOKEN cannot see a multiplier
	   applied to it**, which is how this sat under a contrast canary for a year. color: inherit
	   alone still solves the original problem, and solves it above the bar. */
	.btn .hint {
		color: inherit;
		font-size: inherit;
	}
	.btn {
		/* The vertical padding and the line-height are TOKENS because min-height below is derived from
		   both, and a height that restates them is a height that drifts the first time one is tuned. */
		--btn-pad-y: 8px;
		--btn-line: 1.6;
		padding: var(--btn-pad-y) 16px;
		border: 1px solid var(--border);
		border-radius: var(--radius);
		background: var(--bg3);
		color: var(--text);
		font-family: inherit;
		font-size: 0.875rem;
		/* Pin the line-height so a <button> and an <a class="btn"> render at the same height side by side */
		line-height: var(--btn-line);
		/* ⚠ An ICON-ONLY button is SHORTER than its labelled neighbour without this, and the reason is
		   that line-height sizes a LINE BOX, which only a text run creates (owner, 2026-08-10, with a
		   screenshot of the help panel's Back and X sitting side by side at different heights). A label
		   contributes a run 1.6em tall; an icon alone contributes its own 1em box and nothing else, so
		   the two buttons differ by 0.6em - about 7px at btn-small - while every rule that is supposed
		   to make them match is present and correct. The floor restores the line box the text would
		   have made. It is em, never rem: the a11y rule is that only a font-size is a rem, and em is
		   what makes this track the text-size control through the button's own font-size anyway.
		   The 2px is the 1px border top and bottom, which min-height covers under border-box. */
		min-height: var(--control-h);
		cursor: pointer;
		transition: all var(--motion-fast) var(--motion-ease);
		text-decoration: none;
		/* ⚠ A button is a FLEX row, not an inline-block, and that is what puts a gap between an icon and
		   its label (owner, 2026-08-06, with a screenshot of Save appearance: "missing margin and
		   alignment between icon and text... maybe others too?" - and yes, every icon button in the app).
		   An island's template writes the icon helper, a space, then the label, so there IS a space in
		   the markup; what that space is worth is one word space in the button's font, which against a
		   glyph filling its own 1em box reads as no gap at all. A margin on the icon cannot replace it:
		   an icon-only button must not carry one, and no selector can tell "icon plus a TEXT NODE" from
		   "icon alone", because :only-child counts ELEMENTS and text is not one. Flex can - gap applies
		   between items, and a whitespace-only text run is not a flex item at all, so the markup space
		   stops mattering either way. align-items:center is the alignment half: it centres the icon box
		   against the label instead of leaving it on the text baseline under a hand-tuned vertical-align.
		   ⚠ And this comment may not contain a backtick or a dollar-brace - it lives inside a css tagged
		   template, where both are parsed as code (CLAUDE.md's gotcha list; it cost this round a build). */
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		/* Items may wrap when a button carries an icon, a label and a hint in a narrow column -
		   inline-block used to allow that, and a nowrap flex row would overflow instead. */
		flex-wrap: wrap;
		vertical-align: middle;
	}
	.btn:hover {
		background: var(--border);
	}
	.btn-primary {
		/* See public/style.css for why this is a background-IMAGE over the flat color rather than
		   instead of it: --on-accent's contrast is measured against --accent. */
		background-color: var(--accent);
		background-image: linear-gradient(100deg, var(--accent) 0%, var(--accent-2) 165%);
		border-color: var(--accent);
		color: var(--on-accent);
	}
	.btn-primary:hover {
		background-color: var(--accent-hover);
		/* Leaf elements only - a lift on a card would break every position: fixed descendant. */
		transform: translateY(-1px);
		box-shadow: 0 4px 14px color-mix(in srgb, var(--accent) 34%, transparent);
	}
	.btn-primary:active {
		transform: none;
	}
	.btn-danger {
		background: var(--danger);
		border-color: var(--danger);
		color: var(--on-danger);
	}
	.btn-danger:hover {
		opacity: 0.9;
	}
	/* The affirmative primary action - Approve / Send all. Joined the fragment with the first
	   shadow consumers to draw it, the Queue + Approved list islands (round 92). */
	.btn-success {
		background: var(--success);
		border-color: var(--success);
		color: var(--on-success);
	}
	.btn-success:hover {
		opacity: 0.9;
	}
	/* Destructive but repeated on every row of a list - a solid red per row turns the table into a
	   wall of alarm, so it only commits to red on hover, where the intent is real.
	   ⚠ Since round 322 this is EVERY destructive control that is not the moment of commitment:
	   Unpublish, Remove, Delete, Clear all. Solid red is kept for the button that actually does it -
	   the confirm dialog's, and the account delete armed by a typed word. Weight is a claim about how
	   often a thing is the right one to press, and the confirm owns the safety, never the color.
	   A Stop is not red at all: stopping keeps what was already done. */
	.btn-ghost-danger {
		background: none;
		border-color: var(--border);
		color: var(--text2);
	}
	.btn-ghost-danger:hover {
		background: var(--danger);
		border-color: var(--danger);
		color: var(--on-danger);
	}
	/* A SIDE ROAD on a repeated card (round 335): the actions a card offers besides its main one -
	   Caption, Copy link and Log post on a clip, Change release and Dismiss on a mention - as one line
	   of quiet text with their icons. Promoted from the Clips page's copy when the Content page's
	   mention card became the second. A bordered button per side road is weight charged once per card,
	   which is what made both grids read as clutter. */
	.btn-text {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		padding: 3px 0;
		border: 0;
		background: none;
		font: inherit;
		font-size: 0.8125rem;
		color: var(--text2);
		cursor: pointer;
	}
	.btn-text:hover:not(:disabled) {
		color: var(--text);
	}
	.btn-text:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}
	/* A button that reads as a sentence - a disclosure ("paste a link instead"), a "forget this
	   device". It is a BUTTON and not an anchor because it does something rather than going
	   somewhere, which is the only thing that decides between the two; the underline is what tells
	   the reader it is pressable at all, since nothing else about it looks like a control. Local to
	   the settings island since round 99; joined the fragment in round 189 with its second consumer
	   (the quick-start wizard's collapsed paste box). */
	.btn-link {
		background: none;
		border: none;
		color: var(--text2);
		padding: 6px 4px;
		text-decoration: underline;
		font-family: inherit;
		font-size: inherit;
		cursor: pointer;
	}
	.btn-link:hover {
		color: var(--text);
		background: none;
	}
	/* ⚠ The small variant keeps the DERIVED floor, and that is the point of it: the full-size button
	   now takes the app-wide control height so it lines up with the fields beside it, and a small
	   button forced to 40px would not be small. Its floor is still the icon-only fix - a text run makes
	   a line box and a lone glyph does not, so without a floor the two draw at different heights. */
	.btn-small {
		--btn-pad-y: 4px;
		padding: var(--btn-pad-y) 10px;
		font-size: 0.75rem;
		min-height: calc(var(--btn-line) * 1em + 2 * var(--btn-pad-y) + 2px);
	}
	.btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
	/* Inline button rows; the gap spaces them, so no .btn + .btn sibling margin is needed.
	   .btn-row is the generic one; .queue-actions is the action toolbar under a card. */
	.btn-row {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
	}
	.queue-actions {
		display: flex;
		gap: 8px;
		flex-wrap: wrap;
		margin-bottom: 20px;
	}
`;

/**
 * The busy-button spinner - the shadow-side twin of the global `.btn-busy` block. A button that
 * shows work-in-progress adds `.btn-busy`, whose `::after` draws a spinning ring. The ring is a CSS
 * animation, and **`@keyframes` are tree-scoped**: an animation named in one shadow root cannot see a
 * keyframe defined in another (or in the global sheet), so the keyframes MUST travel in the same
 * fragment and be copied into every shadow root that animates the ring. That is exactly why this is
 * its own fragment rather than a couple of lines folded into `buttonStyles` - a component that draws
 * plain buttons but never a busy one should not carry a keyframe it never uses. Born wired to
 * `<gh-onboarding-page>` (round 89 - its Create-artist submit flips to `.btn-busy` while writing).
 */
export const spinnerStyles = css`
	.btn-busy {
		pointer-events: none;
		opacity: 0.8;
	}
	.btn-busy::after {
		content: "";
		display: inline-block;
		width: 11px;
		height: 11px;
		margin-left: 7px;
		vertical-align: -1px;
		border: 2px solid currentColor;
		border-right-color: transparent;
		border-radius: 50%;
		animation: btn-busy-spin var(--motion-status-loop) linear infinite;
	}
	@keyframes btn-busy-spin {
		to {
			transform: rotate(360deg);
		}
	}
`;

/**
 * The motion vocabulary (round 149, BACKLOG B.4).
 *
 * The durations and the easing are TOKENS in `public/style.css` - custom properties inherit through
 * a shadow boundary, which is the only reason a reduced-motion preference can reach these elements
 * at all (the usual `@media (prefers-reduced-motion) { * { animation: none } }` reset does not
 * cross, and every island here has been shadow since round 100). **Keyframes are the opposite: they
 * are tree-scoped**, so an animation named in one shadow root cannot see a keyframe defined in
 * another. That asymmetry is the whole shape of this file - the values travel by inheritance, the
 * keyframes have to travel as a fragment and be copied into every root that animates.
 *
 * ⚠ What is deliberately NOT here: anything that decorates a static page. The one class below marks
 * a state the app already models honestly and exists to make it *legible*. An animation that says
 * nothing is bytes and battery, and on a page the owner sits in all day it is also noise they cannot
 * turn off.
 *
 * ⚠ It is ONE class on purpose, and the emptiness is the design rather than an unfinished list. A
 * "motion vocabulary" invites writing the whole alphabet up front, and round 73's rule is that an
 * extracted thing with no consumer is invisible to every check this repo runs - it typechecks, it
 * lints, and it is dead. The draft of this fragment carried a `.motion-change` bump for "a verdict
 * chip that just flipped", which sounded obviously useful and had no call site, so it is not here.
 * This grows the way `chromeStyles` and `buttonStyles` grew (rounds 91/92): **the round that needs a
 * class adds it, wired to the consumer that needed it.**
 *
 * ⚠ And it does not cross into the published pages. `linkPage`/`bioPage`/`epk` are measured in
 * milliseconds-to-play and carry their own two hand-written reduced-motion rules; importing a
 * frontend fragment there is already banned for the framework reason, and this is no different.
 */
export const motionStyles = css`
	/* Something ARRIVED. It rises a few pixels rather than sliding a long way: the job is to catch
	   the eye that is already on the page, not to announce itself to someone who left.

	   ⚠ The enter-index knob below is what makes this say something rather than decorate. A list whose
	   items appear together is a list; a list whose items appear IN ORDER is a ranked list, and the
	   one place this ships is the dashboard's attention strip, whose entire round-120 argument is
	   that it is ranked (a promise the app is not keeping outranks a chore waiting for the owner).
	   The stagger renders that ranking, so the motion carries the same information the order does.
	   The delay is capped in the call site, not here - a tenth row must not wait a second. */
	.motion-enter {
		animation: motion-rise var(--motion-slow) var(--motion-ease) both;
		animation-delay: calc(var(--motion-enter-index, 0) * var(--motion-fast));
	}
	@keyframes motion-rise {
		from {
			opacity: 0;
			transform: translateY(6px);
		}
	}
`;

/**
 * Card containers - the shadow-side twin of `.card` / `.appearance-card` / `.subtitle`. All three
 * are drawn by light pages too (`.appearance-card` by the sections/artists/fan-kit studios, `.card`
 * everywhere), so they STAY in `public/style.css`; this is the copy a shadow root can see. Born
 * wired to `<gh-campaign-controls>` (round 88 - its retro modal's `.card` body + `.subtitle`, and
 * the `.appearance-card` it wraps its control block in).
 */
export const cardStyles = css`
	/* ⚠ No :hover here - see public/style.css. The app's .card is a page PANEL, not a tile, and a
	   translucent accent shadow on one composites into the section timeline inside it. */
	.card {
		background: var(--bg2);
		border: 1px solid var(--border);
		border-radius: var(--radius);
		padding: 20px;
		margin-bottom: 20px;
	}
	/* The roster/studio card - tighter radius and padding than a plain .card */
	.appearance-card {
		background: var(--bg2);
		border: 1px solid var(--border);
		border-radius: 10px;
		padding: 14px 16px;
		margin-bottom: 14px;
	}
	.subtitle {
		color: var(--text2);
		margin-bottom: 20px;
		font-size: 0.875rem;
	}
`;

/**
 * THE RECORD CARD - a release, drawn as its cover (round 340's "Page by page" mockup: the cover IS the
 * card, and it plays), and since 2026-09-24 the Campaigns page's cards too (owner: *"the cards doesn't
 * look as pretty as your other cards, as seen on the releases or artists pages"*).
 *
 * ⚠ **One fragment, two roots, and that is the point of it.** A campaign card IS a release's card -
 * the same record, recognized by the same cover - so the two pages drawing it from two hand-copied
 * blocks is the drift `docs/notes/shadow-styling.md` is about: identical the day they are written,
 * apart the first time one of them is touched. The classes keep their `release-` names because that is
 * what they draw.
 */
export const releaseCardStyles = css`
	/* ── Release roster (round 340, "Page by page"): the cover IS the card, and it plays ── */
	.release-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(min(260px, 100%), 1fr));
		gap: 20px;
	}
	.release-card {
		display: flex;
		flex-direction: column;
		background: var(--bg2);
		border: 1px solid var(--border);
		border-radius: var(--radius);
		cursor: pointer;
		transition: border-color var(--motion-fast) var(--motion-ease);
	}
	.release-card:hover,
	.release-card:focus-within {
		border-color: var(--accent);
	}
	/* ⚠ The cover clips ITSELF to the card's top corners, and the card clips nothing: the ⋯ is a
	   popover and escapes any clip, but a card that clips is a trap for the next floating thing. */
	.release-cover {
		position: relative;
		aspect-ratio: 1 / 1;
		border-radius: var(--radius) var(--radius) 0 0;
		overflow: hidden;
		background: var(--chrome-bg);
	}
	.release-cover img,
	.release-cover-fallback {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		object-fit: cover;
		color: var(--chrome-mark);
	}
	/* The when, on the art: a label (two spans and a separator, the header kicker's rule) */
	.release-when {
		position: absolute;
		top: 10px;
		right: 10px;
		margin: 0;
		padding: 3px 9px;
		border-radius: 999px;
		background: color-mix(in srgb, var(--chrome-bg) 78%, transparent);
		color: var(--chrome-text);
		font-size: 0.6875rem;
		white-space: nowrap;
	}
	.release-when span + span::before {
		content: " · ";
	}
	.release-play {
		position: absolute;
		left: 12px;
		bottom: 12px;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 44px;
		height: 44px;
		border: 0;
		border-radius: 50%;
		background: linear-gradient(90deg, var(--accent), var(--accent-mid) 60%, var(--accent-2));
		color: var(--on-accent);
		box-shadow: var(--shadow-pop);
		cursor: pointer;
	}
	.release-card-body {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 12px 14px 14px;
		min-width: 0;
	}
	.release-card-head {
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 0;
	}
	/* The title is the card's own button, so a keyboard reaches "open" on every card */
	.release-title {
		flex: 1;
		min-width: 0;
		padding: 0;
		border: 0;
		background: none;
		color: var(--text);
		font-family: var(--font-display);
		font-stretch: var(--font-display-stretch);
		font-weight: 800;
		font-size: 1.375rem;
		line-height: 1.1;
		text-align: left;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		cursor: pointer;
	}
	/* A hint that is reporting something broken rather than explaining something - today the
	   held release-day list, whose whole problem is that it is otherwise invisible */
	.hint-warn {
		color: var(--warning);
	}
	.release-artist {
		font-size: 0.8125rem;
		color: var(--text2);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.release-now {
		margin: 0;
		font-size: 0.875rem;
	}
	.release-now-silent {
		color: var(--text2);
	}
	.release-now-played {
		color: var(--success);
		font-weight: 600;
	}
	.release-status {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 10px;
		flex-wrap: wrap;
		margin-top: auto;
	}
	.release-stats {
		display: flex;
		gap: 18px;
		margin: 0;
	}
	.release-stats div {
		display: flex;
		flex-direction: column-reverse;
		gap: 1px;
	}
	.release-stats dd {
		margin: 0;
		font-family: var(--font-mono);
		font-size: 1.0625rem;
		font-weight: 500;
	}
	.release-stats dt {
		font-family: var(--font-mono);
		font-size: 0.625rem;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--text2);
	}
	.release-pages {
		display: flex;
		flex-wrap: wrap;
		gap: 4px 14px;
		margin: 0;
		padding: 10px 0 0;
		border-top: 1px solid var(--border);
		list-style: none;
		font-size: 0.75rem;
		color: var(--text2);
	}
	.release-pages li {
		display: inline-flex;
		align-items: center;
		gap: 5px;
	}
	.page-dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: var(--border-strong);
	}
	.release-pages li.live .page-dot {
		background: var(--success);
	}
`;

/**
 * A CONNECTION CARD - the grid they sit in, the header chip line, the muted status sentence, the
 * status "lights" checklist, and the numbered how-to guide with its copy box.
 *
 * ## Why it is a fragment and not settings-page CSS
 *
 * It was settings-page CSS for a hundred rounds, and correctly so: every connection card in the app
 * lived on Settings → Connections. The four SOCIAL connectors moved into the artist workspace
 * (`<gh-artist-connections>`), which is the moment "the page that draws connection cards" stopped
 * being one page. ⚠ **Copying the blocks into the second root was the obvious move and it is the bug
 * class `docs/notes/shadow-styling.md` is entirely about** - two twins that are identical the day
 * they are written and silently diverge after, in a root nobody is looking at.
 *
 * ⚠ **The list is `.connection-rows`** (it was a card grid, `.connection-grid`, and before that
 * `.settings-card-grid`). A class naming the PAGE it was born on is a lie in the second place it
 * renders, and a reader who greps `settings-card-grid` to find out how the artist workspace lays its
 * cards out finds a settings page. Rename at the move, while there are two call sites, not later when
 * there are nine. ⚠ And the rows moved in HERE on 2026-09-24, when Settings took the artist page's
 * layout: a second copy in the second page is exactly the drift this fragment exists to stop.
 *
 * ⚠ **The lights are three states plus unknown, and gray is the honest default**: nothing renders
 * green until the server has said so. Red is reserved for "broken with a known fix" and the fix is
 * what the detail line carries - a red light with a shrug beside it is worse than a gray one.
 */
export const connectionCardStyles = css`
	/* ONE ROW PER CONNECTION (round 349 on the artist page, 2026-09-24 on Settings too): one bordered
	   list, a card per row, laid out as three columns - the name and its chip, the status line, the
	   action - with anything else the card holds (a form, the live checks, the how-to, a mail lane)
	   spanning the row underneath. It lives in this fragment rather than in either page because both
	   roots draw it, which is the whole reason the fragment exists. */
	/* §AM: a guest's connections are disabled together inside one fieldset, which must draw nothing */
	.claim-fieldset {
		margin: 0;
		padding: 0;
		border: 0;
		min-width: 0;
	}
	.connection-rows {
		background: var(--bg2);
		border: 1px solid var(--border);
		border-radius: var(--radius);
	}
	.connection-rows .card {
		display: grid;
		grid-template-columns: minmax(150px, 200px) minmax(0, 1fr) auto;
		column-gap: 20px;
		row-gap: 10px;
		align-items: center;
		margin: 0;
		padding: 16px 18px;
		border: 0;
		border-radius: 0;
		background: none;
		box-shadow: none;
	}
	.connection-rows .card + .card {
		border-top: 1px solid var(--border);
	}
	.connection-rows .card > * {
		grid-column: 1 / -1;
	}
	.connection-rows .card > .card-head {
		grid-column: 1;
		flex-direction: column;
		align-items: flex-start;
		gap: 6px;
		margin: 0;
	}
	.connection-rows .card > .connection-status {
		grid-column: 2;
		margin: 0;
		font-size: 0.8125rem;
	}
	.connection-rows .card > .queue-actions {
		grid-column: 3;
		grid-row: 1;
		justify-self: end;
		margin: 0;
		padding: 0;
	}
	/* A card with a form keeps its buttons under the form they submit */
	.connection-rows .card:has(> .form-group) > .queue-actions {
		grid-column: 1 / -1;
		grid-row: auto;
		justify-self: start;
	}
	.connection-rows .card > .form-group {
		max-width: 460px;
		margin: 0;
	}
	@media (max-width: 760px) {
		.connection-rows .card {
			grid-template-columns: minmax(0, 1fr);
		}
		.connection-rows .card > .card-head,
		.connection-rows .card > .connection-status,
		.connection-rows .card > .queue-actions {
			grid-column: 1;
			grid-row: auto;
			justify-self: start;
		}
	}
	/* Card header: title + status chip on one line */
	.card-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		margin-bottom: 4px;
	}
	.card-head h3 {
		margin: 0;
	}
	/* Muted one-line service status ("Active at https://…") - quieter than body text */
	.connection-status {
		font-size: 0.78125rem;
		color: var(--text2);
		margin: 2px 0 12px;
		overflow-wrap: anywhere;
	}
	/* ── The registrar/console guide and the live status checklist ── */
	.domain-guide {
		margin: 4px 0 14px;
		padding-left: 20px;
		font-size: 0.8125rem;
		color: var(--text2);
		line-height: 1.6;
	}
	.domain-guide li {
		margin-bottom: 8px;
	}
	.domain-guide strong {
		color: var(--text);
	}
	.domain-guide em {
		font-style: normal;
		color: var(--text);
		opacity: 0.85;
	}
	.nameserver-box {
		display: flex;
		align-items: center;
		gap: 14px;
		flex-wrap: wrap;
		margin: 8px 0 2px;
		padding: 10px 12px;
		background: var(--bg);
		border: 1px solid var(--border);
		border-radius: var(--radius);
	}
	.nameserver-box ul {
		list-style: none;
		margin: 0;
		padding: 0;
		font-family: var(--font-mono);
		font-size: 0.8125rem;
		color: var(--text);
	}
	.nameserver-box li {
		line-height: 1.7;
	}
	/* The token guides reuse the box for a single copyable value (a redirect URI, usually) */
	.nameserver-box code {
		font-family: var(--font-mono);
		font-size: 0.78125rem;
		color: var(--text);
		word-break: break-all;
	}
	.domain-checks {
		display: flex;
		flex-direction: column;
		gap: 11px;
		margin: 6px 0 14px;
	}
	.check-row {
		display: flex;
		align-items: center;
		gap: 10px;
	}
	/* The "light": gray until we know, amber while waiting, green + glow when connected */
	.check-dot {
		width: 12px;
		height: 12px;
		border-radius: 50%;
		flex: none;
		background: var(--bg3);
		border: 1px solid var(--border);
		transition:
			background var(--motion-base) var(--motion-ease),
			box-shadow var(--motion-base) var(--motion-ease),
			border-color var(--motion-base) var(--motion-ease);
	}
	.check-dot.checking {
		background: var(--warning);
		border-color: var(--warning);
		animation: check-pulse var(--motion-status-loop) ease-in-out infinite;
	}
	.check-dot.ok {
		background: var(--success);
		border-color: var(--success);
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--success) 22%, transparent);
	}
	/* Red is reserved for "broken with a known fix" - the detail line carries the fix */
	.check-dot.bad {
		background: var(--danger);
		border-color: var(--danger);
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--danger) 22%, transparent);
	}
	@keyframes check-pulse {
		0%,
		100% {
			box-shadow: 0 0 0 0 color-mix(in srgb, var(--warning) 35%, transparent);
		}
		50% {
			box-shadow: 0 0 0 4px color-mix(in srgb, var(--warning) 12%, transparent);
		}
	}
	.check-main {
		display: flex;
		flex-direction: column;
		line-height: 1.35;
	}
	.check-label {
		font-size: 0.84375rem;
		color: var(--text);
	}
	.check-detail {
		font-size: 0.75rem;
		color: var(--text2);
	}
`;

/**
 * Form layout - the shadow-side twin of `.form-group` (+ its label/hint) and the `.form-row` that
 * lays two groups side by side. Both stay global for every light form (`.form-row` is drawn by six
 * pages). Born wired to `<gh-campaign-controls>` (round 88 - the retro modal's recommendations block
 * is a `.form-group` with a `<label>`); `.form-row` joined with `<gh-onboarding-page>` (round 89 -
 * step 1's name field sits in a `.form-row`); `.form-half` (its half-width child) joined with
 * `<gh-discover-page>` (round 93 - the genres/regions pickers sit two-up).
 */
export const formStyles = css`
	.form-group {
		margin-bottom: 14px;
	}
	.form-group label {
		display: block;
		font-size: 0.8125rem;
		color: var(--text2);
		margin-bottom: 4px;
	}
	/* Tighter under a form control, where the label already carries the weight.
	   ⚠ No opacity - see the light-DOM twin in public/style.css. --text2 is already AT the AA floor,
	   so 0.7 of it measured 3.59:1: quiet here is smaller, never fainter. */
	.form-group .hint {
		font-size: 0.6875rem;
	}
	/* A switch and the ? that explains it, on one line. This is the app's shape for a boolean whose
	   explanation is a tooltip rather than a paragraph under it (owner, 2026-08-11) - the toggle
	   carries the label, the mark carries the why, and the row keeps them from stacking. */
	.toggle-row {
		display: inline-flex;
		align-items: center;
		gap: 6px;
	}
	/* Two form-groups side by side (each usually flex:1) */
	.form-row {
		display: flex;
		gap: 16px;
	}
	/* A half-width flex child inside a .form-row (min-width:0 lets it shrink past its content - a
	   gh-multiselect otherwise refuses to narrow). Joined with the discover page, round 93. No
	   backticks in this comment: one would close the css tagged template (the round-86 gotcha). */
	.form-half {
		flex: 1;
		min-width: 0;
	}
	/* ── A card of SETTINGS, one per row: the name on the left, a control sized to what it holds on
	   the right (round 322). ⚠ The stacked label-over-field shape gave a two-digit number the whole
	   card - a 900px box for "20" - and nine of them made one card a page long. A setting that holds
	   a number, an hour or a yes/no is read as a sentence with a blank in it, so the blank is only as
	   wide as its answer and the unit sits after it. The ? stays beside the NAME, never inside a label
	   element, because a label forwards every click in it to its control.
	   The row WRAPS rather than switching layout at a breakpoint: a switch still fits beside its name
	   on a phone, a number and its unit do not, and flex-wrap already knows which is which. ── */
	/* Capped for the same reason a field is: past this width the name and its control drift a
	   screen apart, and the eye has to carry the row across empty card to find its answer. */
	.setting-rows {
		max-width: 680px;
		border-top: 1px solid var(--border);
		margin-bottom: 16px;
	}
	.setting-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 6px 16px;
		padding: 10px 0;
		min-height: 58px;
		border-bottom: 1px solid var(--border);
	}
	.setting-name {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		flex: 1 1 220px;
		min-width: 0;
		font-size: 0.875rem;
		color: var(--text);
	}
	.setting-name label {
		cursor: pointer;
	}
	.setting-control {
		display: flex;
		align-items: center;
		gap: 8px;
		flex: none;
		font-size: 0.8125rem;
		color: var(--text2);
	}
	.setting-control input[type="number"] {
		width: 84px;
		text-align: right;
	}
	/* Reserved even when a row has no unit, so every number box in the card lines up on one edge */
	.setting-unit {
		min-width: 3.75em;
	}
	/* The reason a row's control is disabled. It is visible rather than a ?, because a wall you have
	   to hover to find is a control that looks broken (the app's disabled-with-the-reason rule). */
	.setting-wall {
		flex-basis: 100%;
		margin: 0;
		font-size: 0.75rem;
		color: var(--text2);
	}
`;

/**
 * The form-control base box - the shadow-side twin of the global `input[type=…]/textarea/select`
 * block. This is the one rule a shadow root most conspicuously loses: the global sheet styles bare
 * controls by TYPE selector, and selector CSS does not cross the boundary (the theme TOKENS do, so
 * the colors are already right - it is the box, padding and font that vanish). So any shadow page
 * with a form has to carry this, and it is one fragment rather than a per-page copy for the usual
 * anti-drift reason. `font-family: inherit` matters: a bare control otherwise falls back to the UA
 * monospace/serif, and `inherit` is what pulls the app font across (round 85's search-box lesson).
 * Born wired to `<gh-onboarding-page>` (round 89, its one text input) AND adopted by
 * `<gh-queue-composer>`, which inlined a partial copy (`select`/`textarea` only) in round 87.
 *
 * ⚠ **THE DATE TYPES WERE MISSING FOR NINETY-NINE ROUNDS, AND A TYPE LIST IS WHY** (round 189,
 * owner-reported). This rule enumerates the types it styles, so a type nobody thought to add is not
 * "unstyled" in any way a reader can see - it renders as the browser's own widget, which is a
 * complete, confident-looking control. Quick start's Release Date sat beside a Type select and a
 * Genre input, two-thirds the height, in the UA's font, and every other date field in the app
 * (`sent`, `content`, `shortsStudio`, and two more in `releases`) had the same defect. **An
 * allowlist's failure mode is a plausible-looking wrong answer, never a blank.** The neighbours are
 * added with it: `time` and `datetime-local` are the same widget family and would fail identically
 * the first time one is used.
 *
 * ⚠ **AND IT HAPPENED AGAIN THE FIRST TIME SOMEBODY REACHED FOR A NEW TYPE** (2026-08-10, owner, of
 * the Outlets search box the same hour it changed from `text` to `search`: *"way too small in height
 * and looks unstyled"*). One character in a template, and a field that had looked right for two
 * hundred rounds fell out of this list. Round 189's own remedy is what shortens the next one: `search`
 * goes in with its neighbours - and `search` needs one more line than the others, because WebKit
 * gives it `appearance: textfield` with its own tiny box and a decoration button that ignores every
 * property above. **When a rule works by listing what it applies to, adding to the list is part of
 * using a new value, not a follow-up.**
 *
 * ⚠ Two things a date input needs that a text input does not, and both are one-liners here so no
 * call site has to know them. **`min-height`**, because the native widget does not derive its box
 * from `padding` the way a text input does - without it the field is a few pixels short of its
 * neighbours, which is the visible half of the bug above. And **`::-webkit-calendar-picker-indicator`
 * takes its color from `color-scheme`, not from `color`** - `:root` already stamps `color-scheme`
 * per theme and it INHERITS through shadow boundaries (round 148's rule: an ambient condition
 * reaches a shadow-rooted element only through an inherited property), so the glyph is already
 * right; what it needs is the cursor, since the whole indicator is a button.
 */
export const controlStyles = css`
	input[type="text"],
	input[type="search"],
	input[type="url"],
	input[type="email"],
	input[type="number"],
	input[type="password"],
	input[type="tel"],
	input[type="date"],
	input[type="time"],
	input[type="datetime-local"],
	textarea,
	select {
		width: 100%;
		padding: 8px 12px;
		border: 1px solid var(--border-strong);
		border-radius: var(--radius);
		background: var(--bg);
		color: var(--text);
		font-size: 0.875rem;
		font-family: inherit;
	}
	/* ⚠ EVERY control in this app is 37px tall, and it is stated here rather than left to each type's
	   own content box (owner, 2026-08-11, twice in one session: "dropdowns are taller than other
	   fields" and then, of the new phone field, "heights are not aligned here").

	   The heights used to be an ACCIDENT of three different things: a text input took the UA's normal
	   line-height, a date input took the native widget's own box, and anything drawn in a shadow root
	   inherited the page's 1.6 line-height and came out three pixels taller than all of them. Three
	   rules were already pinning 37px one type at a time - date, time, datetime-local, search - which
	   is the shape round 189 warned about from the other side: when a rule works by listing what it
	   applies to, the omissions are invisible, and the omission here was the plain text input that
	   every other control is measured against.

	   line-height is half the fix and the non-obvious half: a min-height cannot make a box SHORTER, so
	   an inherited 1.6 (22.4px of text plus 18px of box) still won at 40px. The normal keyword is what a
	   native input would have used anyway; textarea is deliberately not in this list, because there the
	   inherited leading is what makes several lines readable.
	   (No backticks in this comment - one ends the css template. Met twice more this session.) */
	input[type="text"],
	input[type="search"],
	input[type="url"],
	input[type="email"],
	input[type="number"],
	input[type="password"],
	input[type="tel"],
	input[type="date"],
	input[type="time"],
	input[type="datetime-local"],
	input[type="color"] {
		min-height: var(--control-h);
		line-height: normal;
	}
	/* ⚠ A COLOR input is a control like any other and was in none of the lists above - round 189's
	   allowlist finding, met again in the row that reported it (the press kit's Appearance strip, where
	   the swatch sat between two pickers at a third height). Its inner swatch fills the box, so the
	   padding is a hairline rather than the 8/12 the text inputs take; the width is content-sized
	   because a color box is a chip, not a field you type in. Every page that draws one carries its own
	   size rule beside it, which still wins - this is the floor under those. */
	input[type="color"] {
		width: 56px;
		padding: 3px;
		cursor: pointer;
	}
	/* A search input wears a UA appearance that overrides the box above - it keeps its own height and
	   its own inner spacing whatever the padding says. Neutralising the appearance is what makes it
	   the same control as its neighbours; the clear button keeps its own drawing, which is the point
	   of using the type at all. */
	input[type="search"] {
		appearance: none;
		-webkit-appearance: none;
	}
	input[type="search"]::-webkit-search-cancel-button {
		cursor: pointer;
	}
	input[type="date"]::-webkit-calendar-picker-indicator,
	input[type="time"]::-webkit-calendar-picker-indicator,
	input[type="datetime-local"]::-webkit-calendar-picker-indicator {
		cursor: pointer;
	}
	textarea {
		resize: vertical;
	}
	select {
		appearance: auto;
	}
`;

/**
 * An exclusive choice of two or three, drawn as CARDS (2026-08-11).
 *
 * The owner reported the Sender Identity card - *"this could be improved in terms of UI"* over a
 * screenshot of two bare UA radios in the platform's blue, sitting flush against the left edge under
 * a styled label. They were the last controls in the app wearing no styling at all, and the reason is
 * instructive: `<gh-toggle>` took the checkboxes, `<gh-select>` took the pickers, and a radio group
 * belongs to neither. **It is not a boolean** (a switch cannot express "one of three"), and **it is
 * not a picker** (`<gh-select>` hides the alternatives behind a click, which is wrong when there are
 * two of them and the CHOICE is the point - a sender writing as the artist and a sender writing as
 * their label produce different pitches, and that is a decision to weigh, not a value to set).
 *
 * ⚠ **The native `input[type="radio"]` stays**, and only its surroundings are ours. It brings arrow-key
 * navigation within the group, the roving tabstop, the announced group semantics and `form.reset()` -
 * every one of which a div with `role="radio"` would have to re-implement, and one of which it would
 * get wrong. `accent-color` is the whole of the recolor: one property, respected by the platform
 * widget, and it follows `color-scheme` in both themes for free.
 *
 * ⚠ **`:has()` is what makes the card light up**, which is the rule that could not be written before
 * it: the state lives on the INPUT and the box being painted is its ancestor. The tint is a
 * `color-mix` of the accent rather than a picked color, so it is right in all four palettes
 * (`tests/theme.test.ts`).
 *
 * The shorts studio's `.shorts-capstyle` is the same idea grown page-locally, and it is deliberately
 * NOT rewritten in terms of this: that vocabulary is entangled with `.shorts-picked`, which two other
 * kinds of card there also wear. If a third page needs choice cards, unify then - a fragment with two
 * consumers is a pattern, a fragment with one is a guess.
 */
export const radioStyles = css`
	.radio-group {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}
	/* Stack them when each choice carries an explaining line - a row of two paragraphs is a column
	   pretending not to be one. The caller adds it; the default is the row above. */
	.radio-group.radio-stack {
		flex-direction: column;
		align-items: stretch;
	}
	/* ⚠ EVERY rule below is qualified by the group, and that is not tidiness - it is the difference
	   between this fragment working and silently losing (2026-08-11, caught by the owner on the first
	   screenshot of it: the radio sat above the text and both lines came out the same muted size).
	   These cards live inside a form-group, and formStyles says .form-group label - display block,
	   0.8125rem, the muted color - which at (0,1,1) OUTRANKS a bare .radio-card at (0,1,0) however
	   late it is composed. So the card was a block, its own display and color never applied, and the
	   two text lines differed by one pixel of font-size and nothing else. The group qualifier takes
	   these to (0,2,0), which wins on merit rather than on ordering.
	   ⚠ The general form is this file's oldest lesson in a new place: a rule that looks correct and
	   does not REACH is invisible in review, because the markup and the CSS are both right. When a
	   fragment styles an element that some other fragment also names, check the arithmetic. */
	.radio-group .radio-card {
		display: flex;
		align-items: center;
		gap: 10px;
		box-sizing: border-box;
		/* The app's control box, same as controlStyles - a choice card sits in a form beside inputs.
		   The zero margin is the form-group label rule's 4px being taken back. */
		min-height: var(--control-h);
		margin-bottom: 0;
		padding: 8px 12px;
		border: 1px solid var(--border-strong);
		border-radius: var(--radius);
		background: var(--bg);
		color: var(--text);
		cursor: pointer;
		transition:
			border-color var(--motion-fast) var(--motion-ease),
			background var(--motion-fast) var(--motion-ease);
	}
	.radio-group .radio-card:hover {
		border-color: var(--accent);
	}
	.radio-group .radio-card:has(input:checked) {
		border-color: var(--accent);
		background: color-mix(in srgb, var(--accent) 10%, transparent);
	}
	.radio-group .radio-card input[type="radio"] {
		accent-color: var(--accent);
		flex-shrink: 0;
		margin: 0;
		cursor: pointer;
	}
	/* The ring belongs to the CARD when the focus is on the radio inside it: the input is a 13px dot
	   in the corner of a box the reader perceives as the control, and a ring around the dot alone is
	   the same "where am I" problem focusStyles exists to end. */
	.radio-group .radio-card:has(input:focus-visible) {
		outline: var(--focus-ring-width) solid var(--focus-ring-color);
		outline-offset: var(--focus-ring-offset);
	}
	.radio-group .radio-card-text {
		display: flex;
		flex-direction: column;
		gap: 1px;
		min-width: 0;
	}
	/* ⚠ The choice and the explanation are two KINDS of text and have to look it (owner, 2026-08-11:
	   "it doesn't look nice that 'I am the artist' and the subtext have identical font weight and
	   presentation"). The label is the thing being chosen - body size, full-strength color, semibold;
	   the line under it is why you would - smaller, muted, regular. Same relationship as a picker row
	   and its hint, which is deliberate: they are the same object drawn open rather than closed. */
	.radio-group .radio-card-label {
		font-size: 0.875rem;
		font-weight: 600;
		color: var(--text);
	}
	.radio-group .radio-card-hint {
		font-size: 0.75rem;
		font-weight: 400;
		line-height: 1.4;
		color: var(--text2);
	}
`;

/**
 * The modal overlay - the shadow-side twin of `.modal` / `.modal-content` / `.modal-wide` /
 * `.modal-actions`. Stays global for every static-chrome modal; born wired to
 * `<gh-campaign-controls>` (round 88 - the retrospective modal it renders from state while open).
 *
 * `position: fixed` on `.modal` is unaffected by the shadow boundary - a shadow root does not
 * create a containing block, so the overlay still covers the viewport and stacks by its z-index
 * exactly as it did in the light tree (the host sets no stacking context). The global keydown
 * `.hidden` sweep simply never reaches a shadow modal (`querySelectorAll` does not pierce), which
 * is fine: the element already closes this modal through STATE on Escape.
 */
export const modalStyles = css`
	/* ⚠ GRID, not flex, and it is the corner exit that decided it (owner, 2026-08-10: *"I want the X
	   at the very edge"*, with a reference shot of a circle straddling a dialog's corner). The mark
	   cannot live inside .modal-content: that box is a scroll container, which clips its children
	   strictly, so anything hung over the edge is simply cut off. So it is a SIBLING - and a sibling
	   needs a box the size of the dialog to hang off. With place-content centered, the single implicit
	   track sizes to the largest item, which is .modal-content, so both children share a cell that IS
	   the dialog's own box and place-self addresses its corners. Flex cannot do this: in a single-line
	   flex container the line's cross size is the CONTAINER's, so align-self start means the top of
	   the viewport rather than the top of the dialog.
	   ⚠ No backticks in this comment, and that is not a style choice - one ends the tagged template.
	   It has now cost four rounds. */
	.modal {
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.6);
		display: grid;
		place-content: center;
		z-index: 10000;
	}
	.modal > * {
		grid-area: 1 / 1;
	}
	.modal-content {
		background: var(--bg2);
		border: 1px solid var(--border);
		border-radius: var(--radius);
		padding: 24px;
		width: min(640px, calc(100vw - 32px));
		max-height: 85vh;
		overflow-y: auto;
	}
	.modal-wide {
		width: min(880px, calc(100vw - 32px));
	}
	/* The corner exit (owner, 2026-08-10) - a CIRCLED mark straddling the dialog's top edge, the shape
	   the tip-corner badge below wears on a button, which is the reference the owner sent.
	   ⚠ It is a SIBLING of .modal-content, never a child, and the grid above is what makes that work -
	   see the note there. A child cannot hang over the edge because the content box scrolls, and a
	   scroll container clips absolutely.
	   ⚠ Because it is outside the scroll box it needs no sticky and cannot scroll away: the whole
	   complaint (the only exit is a Close button below the fold on a long form) is answered by the
	   mark simply not being part of what scrolls.
	   The modal-close helper in src/frontend/modalClose.ts is the one place it is written. */
	.modal-x {
		place-self: start end;
		/* Centred ON the corner rather than tucked inside it. Half the button hangs over the backdrop,
		   which is what makes it read as belonging to the dialog's edge instead of to its content. */
		/* ⚠ 40%, not 50%: the mark hangs 13.6px past the dialog's right edge and a full-width dialog on a
		   phone leaves exactly 16px of margin there (min(640px, 100vw - 32px)). At 50% it would be 17px
		   and the document would scroll sideways - which tests/ui/mobile.test.ts is the only thing that
		   would ever say so. */
		transform: translate(40%, -40%);
		z-index: 2;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 34px;
		height: 34px;
		padding: 0;
		border: 1px solid var(--border);
		border-radius: 50%;
		background: var(--bg2);
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
		color: var(--text2);
		cursor: pointer;
		transition:
			background var(--motion-fast) var(--motion-ease),
			color var(--motion-fast) var(--motion-ease);
	}
	.modal-x:hover {
		background: var(--danger);
		border-color: var(--danger);
		color: var(--on-danger);
	}
	/* ── ONE SHAPE FOR EVERY DIALOG (round 352, the "Add and edit dialogs" mockups, owner 2026-09-24:
	   "Yeah, I love every mockup, build it all"). The title names the act, the line under it names the
	   record, and the footer NEVER SCROLLS AWAY: it sticks to the dialog's bottom edge, drawn as its
	   own strip, with the saved state or a quiet extra at its left and Cancel then the one primary at
	   its right. ⚠ The negative offsets are the content box's own 24px padding: the strip is laid out
	   edge to edge and sticks to the scrollport, not to the padding line inside it. ── */
	.modal-content > h3:first-of-type,
	.modal-content > form > h3:first-of-type,
	.modal-content .modal-title {
		margin: 0 0 4px;
		padding-right: 18px;
		font-family: var(--font-display);
		font-stretch: var(--font-display-stretch);
		font-weight: 800;
		font-size: 1.375rem;
		line-height: 1.15;
	}
	.modal-content > .subtitle,
	.modal-content > form > .subtitle {
		margin: 0 0 18px;
	}
	.modal-actions {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
		position: sticky;
		bottom: -24px;
		z-index: 2;
		margin: 20px -24px -24px;
		padding: 12px 24px;
		border-top: 1px solid var(--border);
		background: var(--bg3);
		border-radius: 0 0 var(--radius) var(--radius);
	}
	/* The unsaved dot beside the footer's state */
	.modal-actions .dot-warn {
		display: inline-block;
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: var(--warning);
	}
	/* The footer's left side: the saved state, or a quiet extra (Preview, Copy, a menu) */
	.modal-actions > .actions-start {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-right: auto;
		font-size: 0.8125rem;
		color: var(--text2);
	}
`;

/**
 * THE BIG EDITOR (round 353): a fixed frame - the head naming the record, the section list at the
 * left, the chosen section scrolling on its own at the right, and the footer that never moves. The
 * release, artist and outlet editors compose it with `modalStyles`; `frontend/editorNav.ts` draws
 * the list.
 * ⚠ **The frame does not scroll; the pane does.** The other dialogs scroll their whole content box
 * and pin the footer with sticky, but a section list that scrolled away with the fields would leave
 * the reader in a long pane with no way to see the others - so here the content box is a flex
 * column that clips, and only `.editor-pane-scroll` overflows.
 */
export const editorShellStyles = css`
	.modal-content.modal-editor {
		width: min(1080px, calc(100vw - 32px));
		height: min(800px, 88vh);
		max-height: 88vh;
		padding: 0;
		overflow: hidden;
		display: flex;
		flex-direction: column;
	}
	.modal-editor > form {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
	}
	.editor-head {
		display: flex;
		align-items: center;
		gap: 14px;
		padding: 18px 24px 14px;
		border-bottom: 1px solid var(--border);
	}
	.editor-head img,
	.editor-head .editor-head-empty {
		flex: none;
		width: 46px;
		height: 46px;
		border-radius: 8px;
		object-fit: cover;
		background: var(--bg3);
	}
	.editor-head.round img,
	.editor-head.round .editor-head-empty {
		border-radius: 50%;
	}
	/* A record with no picture of its own shows its kind's drawing in the tile (an outlet) */
	.editor-head .editor-head-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--text2);
		font-size: 1.375rem;
	}
	.editor-head h3 {
		margin: 0;
		font-family: var(--font-display);
		font-stretch: var(--font-display-stretch);
		font-weight: 800;
		font-size: 1.375rem;
		line-height: 1.15;
	}
	.editor-head .subtitle {
		margin: 2px 0 0;
		font-size: 0.8125rem;
	}
	.editor-shell {
		flex: 1;
		min-height: 0;
		display: grid;
		grid-template-columns: 210px minmax(0, 1fr);
	}
	.editor-nav {
		display: flex;
		flex-direction: column;
		gap: 2px;
		padding: 14px 10px;
		border-right: 1px solid var(--border);
		background: var(--bg3);
		overflow-y: auto;
	}
	.editor-nav-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		padding: 9px 12px;
		border: 0;
		border-radius: calc(var(--radius) - 2px);
		background: none;
		color: var(--text);
		font: inherit;
		font-size: 0.875rem;
		text-align: left;
		cursor: pointer;
	}
	.editor-nav-item:hover {
		background: color-mix(in srgb, var(--bg2) 70%, transparent);
	}
	.editor-nav-item.active {
		background: var(--bg2);
		font-weight: 600;
		box-shadow: inset 3px 0 0 var(--accent);
	}
	.editor-nav-count {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		color: var(--text2);
	}
	.editor-nav-done {
		display: inline-flex;
		color: var(--success);
	}
	.editor-nav-note {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		font-size: 0.6875rem;
		color: var(--text2);
		white-space: nowrap;
	}
	.editor-nav-note .dot-warn {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: var(--warning);
	}
	.editor-pane-scroll {
		min-width: 0;
		overflow-y: auto;
		padding: 20px 24px 24px;
	}
	.editor-pane[hidden] {
		display: none;
	}
	.editor-pane-title {
		margin: 0 0 14px;
		font-family: var(--font-display);
		font-stretch: var(--font-display-stretch);
		font-weight: 700;
		font-size: 1.125rem;
	}
	/* The footer is part of the frame here, not a sticky strip over scrolling content */
	.modal-editor .modal-actions {
		position: static;
		margin: 0;
		padding: 12px 24px;
		border-radius: 0;
	}
	/* A phone: the list becomes a row of tabs over the pane */
	@media (max-width: 720px) {
		.editor-shell {
			grid-template-columns: minmax(0, 1fr);
			grid-template-rows: auto minmax(0, 1fr);
		}
		.editor-nav {
			flex-direction: row;
			overflow-x: auto;
			border-right: 0;
			border-bottom: 1px solid var(--border);
			padding: 8px;
		}
		.editor-nav-item {
			flex: none;
		}
		.editor-nav-item.active {
			box-shadow: inset 0 -3px 0 var(--accent);
		}
	}
`;

/**
 * THE LANGUAGE TABS over a section of localized fields (round 354, `localeTabs` in
 * `frontend/localeCard.ts`). ONE fragment, composed by the release and artist editors - the per-card
 * locale CSS was copied into three islands, and a fourth copy is how twins drift.
 */
export const localeTabStyles = css`
	.locale-tabs {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 8px 16px;
		margin-bottom: 16px;
		border-bottom: 1px solid var(--border);
	}
	.locale-tab-row {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 2px;
	}
	.locale-tab {
		padding: 8px 12px;
		margin-bottom: -1px;
		border: 0;
		border-bottom: 2px solid transparent;
		background: none;
		color: var(--text2);
		font: inherit;
		font-size: 0.8125rem;
		cursor: pointer;
	}
	.locale-tab.active {
		color: var(--text);
		font-weight: 600;
		border-bottom-color: var(--accent);
	}
	.locale-tab-remove {
		display: inline-flex;
		align-items: center;
		padding: 4px;
		margin-left: -6px;
		border: 0;
		background: none;
		color: var(--text2);
		cursor: pointer;
	}
	.locale-tab-remove:hover {
		color: var(--danger);
	}
	.locale-tab-add {
		width: 180px;
		margin-left: 8px;
		white-space: nowrap;
	}
	.locale-translate-empty {
		margin-bottom: 6px;
	}
`;

/**
 * The pitch card - the chrome the Queue and Approved islands BOTH draw around a pitch, the CSS half
 * of what `pitchCard.ts` already shares as markup. Unlike every fragment above, this is NOT a twin
 * of a still-global block: the whole `.queue-card` namespace was used only by those two pages (grep-
 * confirmed), so it MOVED here wholesale when they flipped (round 92) and this is now its sole home.
 * It lives in this file rather than duplicated into two `static styles` for the same anti-drift reason
 * the file exists - two identical copies of a fifteen-rule block is exactly how shadow styling drifts.
 *
 * `.queue-card-header h4` re-sets `margin: 0`: it neutralises `chromeStyles`' generic `h4` margin
 * (which both consumers also compose) so the card title sits flush the way the `*`-reset kept it in
 * light DOM. The `.email-preview`/`.mail-*`/`.email-editor` rules are the read-vs-edit swap
 * `pitchCard.ts` renders; `.queue-card textarea`/`input` re-tighten the shared `controlStyles` box.
 */
export const pitchCardStyles = css`
	.queue-card {
		background: var(--bg2);
		border: 1px solid var(--border);
		border-radius: var(--radius);
		padding: 20px;
		margin-bottom: 16px;
	}
	.queue-card-header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: 12px;
	}
	.queue-card-header h4 {
		font-size: 1rem;
		margin: 0;
	}
	/* Long outlet names must not shove the badges off the card */
	.queue-card-title {
		min-width: 0;
	}
	/* ⚠ **THE TITLE ABSORBS THE ROW'S SLACK, and this rule lives HERE for an empirical reason rather
	   than a tidy one** (owner reported the gap three times; 2026-08-22 settled it in DevTools). The
	   identical rule sat in queue.ts's own style block, shipped in the bundle, higher specificity,
	   last in the array - and the computed display on the element was still block. What gave it away
	   was the SIBLING: queue-select gets its display from that same local block and had no flex
	   badge either, while queue-card-badges - defined in THIS fragment - did. **Two rules from one
	   stylesheet dead, every rule from another alive, on the same element.**
	   ⚠ So the bug was never the CSS. Three fixes were written into a sheet that does not reach the
	   element, and each looked wrong for a different imagined reason. **When a correct rule does not
	   apply, stop editing it and ask which STYLESHEET the element is actually reading** - the fastest
	   tell is a neighbouring rule from a different source that does work.
	   ⚠ Safe for Approved, which composes this fragment without a selection: with two children instead
	   of three the title still takes the slack and the badges stay pinned right, which is what
	   space-between already did there. */
	.queue-card-header .queue-card-title {
		flex: 1 1 0;
	}
	/* The tick is the one element here with a fixed natural size. Saying so removes the whole class of
	   gap rather than the instance: with space-between, ANY child that grows pushes the others apart. */
	.queue-card-header .queue-select {
		display: flex;
		align-items: center;
		margin-right: 10px;
		flex: 0 0 auto;
		/* ⚠ The tick centres against the TITLE'S FIRST LINE, not against the top of the row (owner,
		   2026-08-22: the title starts a few px below the tick). The header is align-items: flex-start,
		   so both children start at the same y - but a 17px checkbox and a line of 16px text with
		   line-height do not have their optical centres in the same place, and the eye reads that as
		   misalignment rather than as two correctly-topped boxes.
		   The old fix was padding-top: 2px, which is a number tuned to one font at one text scale and
		   drifts the moment either moves. Giving this box the height of ONE LINE and centring inside it
		   ties the tick to the text it labels, so it survives the text-size control (a11y) and any
		   future change to the heading's size. The rem value is the fallback for browsers without the
		   lh unit; where lh is supported it wins and is exact. */
		min-height: 1.6rem;
		min-height: 1lh;
	}
	.queue-card-badges {
		display: flex;
		align-items: center;
		gap: 6px;
		flex-wrap: wrap;
		justify-content: flex-end;
		flex: none;
	}
	/* ⚠ **THIS LINE HOLDS AN EMAIL ADDRESS, WHICH IS ONE UNBREAKABLE TOKEN** (round 258). It is the
	   recipient, the outlet's host and the release title, and the first of those is a word no browser
	   may break: an ordinary press address at an ordinary domain is around 58 characters that cannot
	   wrap, so on a 390px phone it painted 81px past the window and the WHOLE DOCUMENT scrolled
	   sideways - on Queue and Approved, the two pages that share this card. (No backticks in here:
	   this is a css tagged template and one would end it. Sixth recurrence, per CLAUDE.md.)
	   ⚠ **What made it invisible for a round and a half is that the box never moved.** Text overflows
	   its container without changing the container's rectangle, so every element-rect scan reports a
	   perfectly fitting page while the document's own scrollWidth says 471. Only the document reading
	   catches it, which is exactly why tests/ui/mobile.test.ts asserts on the document.
	   ⚠ anywhere rather than break-word: the two differ on whether the break is allowed to shrink
	   the element's MIN-CONTENT width, and inside a flex row (which this card's header is) only the
	   one that shrinks it actually prevents the overflow. */
	.queue-card .meta {
		font-size: 0.8125rem;
		color: var(--text2);
		margin-bottom: 12px;
		overflow-wrap: anywhere;
	}
	/* The pitch, rendered as the email it will become - not as a pair of form fields */
	.queue-card .email-preview {
		background: var(--bg);
		border: 1px solid var(--border);
		border-radius: var(--radius);
		padding: 14px 16px;
		margin-bottom: 12px;
	}
	.queue-card .mail-subject {
		font-weight: 600;
		font-size: 0.90625rem;
		padding-bottom: 10px;
		margin-bottom: 10px;
		border-bottom: 1px solid var(--border);
	}
	.queue-card .mail-body {
		white-space: pre-wrap;
		font-size: 0.84375rem;
		line-height: 1.65;
		color: var(--text2);
		max-height: 260px;
		overflow-y: auto;
	}
	.queue-card .email-editor {
		margin-bottom: 12px;
	}
	.queue-card .actions {
		display: flex;
		gap: 8px;
		flex-wrap: wrap;
		align-items: center;
	}
	/* Edit in queue: the fields sit a touch tighter than the shared control box */
	.queue-card textarea {
		width: 100%;
		margin-bottom: 8px;
	}
	.queue-card input[type="text"] {
		margin-bottom: 8px;
	}
	/* Focus ring for j/k keyboard nav (Queue) */
	.queue-card:focus {
		outline: var(--focus-ring-width) solid var(--focus-ring-color);
		outline-offset: var(--focus-ring-offset);
	}
`;

/**
 * A PLAN WALL worn by the control it is about (round 190) - the badge in a disabled button's corner,
 * and the positioned wrapper that lets it sit there and still be pressable.
 *
 * ⚠ **Born with two consumers on purpose**: the Artists page's "+ Add Artist" (the plan's artist cap)
 * and the Approved page's "Send all" (the monthly pitch cap). It started as a block inside
 * `artists.ts` and moved here the moment the second one needed it - round 73's rule in the direction
 * it is usually easier to get wrong: a fragment must not be extracted before it has a consumer, and
 * must not be COPIED once it has two.
 *
 * The shape is the owner's (2026-08-03): a disabled button with a separate "See plans & upgrade"
 * button beside it reads as two independent actions, and nothing in that arrangement says the second
 * exists BECAUSE the first is dead. A warning badge on the corner is a shape a reader already knows
 * means *this control has something wrong with it*, and it cannot be read as a separate feature
 * because it is physically part of the button.
 *
 * Two rules a call site must not get wrong, both of which follow from one browser fact:
 *
 * ⚠ **A disabled button fires NO pointer events**, so the tooltip goes on `.cap-wrap` - the attribute
 * on the button would never once show - and the badge is a SIBLING of the button rather than a child,
 * because a child of a disabled button is unclickable too. Being a sibling inside the positioned
 * wrapper is what lets it both sit on the corner and be pressed.
 *
 * ⚠ **Both of those were right and the tooltip still never showed** (2026-08-04, owner: "I've
 * hovered it with the mouse forever") - and the cause was nothing to do with this pattern, which is
 * the part worth recording. A `mouseover` whose target and relatedTarget are in the SAME shadow tree
 * is never dispatched outside it, so `tooltip.ts`'s document-level listener could not see a pointer
 * moving around inside an island at all. The reason appeared over the ICON, because `<gh-icon>` has
 * its own root and moving into it crosses a boundary. `tooltip.ts` binds per shadow root now.
 *
 * ⚠ **The lesson is about the diagnosis, not the wall**: "a disabled button fires no pointer events"
 * is true, it is written directly above, and it is a completely convincing explanation for a symptom
 * it did not cause - the same trace fits both stories until you look at which ROOT the event stopped
 * at. A `pointer-events: none` fix was written, tested, and passed; removing it again passed too.
 * **When a plausible cause is already documented next to the bug, the risk is confirming it rather
 * than testing it.** `tests/ui/artists.test.ts` hovers the button's CENTER, since the icon is the
 * one spot that passes either way.
 */
export const planWallStyles = css`
	.cap-wrap {
		position: relative;
		display: inline-block;
	}
	/* ⚠ **The GLYPH has room now, and the circle is the size it always was** (owner, 2026-08-28: first
	   "generally decrease the size of the warning/alert icon within these buttons", then - after a
	   round that shrank the whole badge - "what I meant was that I wanted more padding between the
	   warning icon and the corners around it").
	   ⚠ **THE FINDING, and it is why the first attempt made it worse: what a reader calls "too big" is
	   usually a RATIO, and shrinking both terms holds the ratio exactly where it was.** 21px outer minus
	   the 2px ring is a 17px field, and a 13px triangle left 2px around it - which is what read as
	   cramped. Taking the badge to 18 and the glyph to 11 kept 0.61 and closed the gap to 1.5px: strictly
	   tighter, from a change made in the name of loosening it. **Before resizing a mark, work out which
	   of the two numbers the complaint is actually about.** The glyph is 10px and the field is 17, so
	   there are 3.5px on every side.
	   ⚠ The ring stays 2px and the offsets stay half the width: that is what makes the badge read as
	   sitting ON the button rather than in it, and a corner mark's offset is a fact about its size. */
	.cap-flag {
		position: absolute;
		top: -7px;
		right: -7px;
		width: 21px;
		height: 21px;
		padding: 0;
		display: grid;
		place-items: center;
		border-radius: 50%;
		background: var(--warning);
		color: var(--on-warning);
		/* A ring in the PAGE color, so the badge reads as sitting ON TOP of the button rather than as
		   part of its fill - the same trick a notification dot uses. */
		border: 2px solid var(--bg);
		cursor: pointer;
	}
	.cap-flag:hover {
		filter: brightness(1.08);
	}
	.cap-flag:focus-visible {
		outline: var(--focus-ring-width) solid var(--focus-ring-color);
		outline-offset: var(--focus-ring-offset);
	}
	/* The panel-sized version of the same fact - see planWall.ts's planOffer for why it is an OFFER
	   and not a warning. ⚠ Accent, never danger: this app spends red on things that DESTROY
	   something, and a plan wall takes nothing away. */
	.plan-offer {
		display: flex;
		align-items: center;
		gap: 10px;
		flex-wrap: wrap;
		margin-top: 12px;
		padding: 11px 14px;
		border: 1px solid color-mix(in srgb, var(--accent) 35%, var(--border));
		border-radius: var(--radius);
		background: color-mix(in srgb, var(--accent) 8%, transparent);
		color: var(--text);
		font-size: 0.875rem;
	}
	.plan-offer span {
		flex: 1;
		min-width: 220px;
	}
	.plan-offer gh-icon {
		color: var(--accent);
		flex: none;
	}
	/* ── The limit meters (round 284). They live in this fragment rather than in a component because
	   every surface that can be WALLED is also a surface that can be METERED, and those are the pages
	   already composing these rules - a second fragment would be imported by exactly the same list.
	   ⚠ No backticks anywhere in here: one ends the css template literal. */
	.plan-meter {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
		margin: 8px 0 0;
		font-size: 0.8125rem;
		color: var(--text2);
	}
	/* The track is a fixed width rather than a flexible one: it sits in a text line, and a bar that
	   grows with its container turns a small reading into a page-wide chart of somebody's allowance. */
	.plan-meter-bar {
		flex: none;
		width: 84px;
		height: 6px;
		border-radius: 3px;
		background: color-mix(in srgb, var(--text2) 22%, transparent);
		overflow: hidden;
	}
	.plan-meter-bar > span {
		display: block;
		height: 100%;
		border-radius: 3px;
		background: var(--accent);
		/* A token, never a literal - a hardcoded duration is one prefers-reduced-motion cannot reach. */
		transition: width var(--motion-base) var(--motion-ease);
	}
	/* ⚠ WARNING, never danger, and it is the rule planOffer states: this app spends red on things that
	   DESTROY something, and an allowance running out takes nothing away. */
	.plan-meter-low .plan-meter-bar > span {
		background: var(--warning);
	}
	.plan-meter-low .plan-meter-text {
		color: var(--text);
	}
	.plan-meter-text {
		min-width: 0;
	}
	/* The way out reads as a link rather than a button: it appears inside a sentence about a number,
	   and a filled button there would out-shout the primary action on whatever page this is sitting on
	   - which on the compose page is the one that actually sends the pitches. */
	.plan-meter-more {
		border: 0;
		background: none;
		padding: 0;
		font: inherit;
		color: var(--accent);
		text-decoration: underline;
		cursor: pointer;
	}
	.plan-meter-more:focus-visible {
		outline: var(--focus-ring-width) solid var(--focus-ring-color);
		outline-offset: var(--focus-ring-offset);
	}
`;

/**
 * Disclosures, in two weights: the plain `details.accordion` and the heavier `details.editor-section`
 * an edit modal is built out of.
 *
 * ⚠ **This fragment exists because the same 33 lines were hand-copied into three page islands, and
 * that is the bug class `src/frontend/CLAUDE.md` names** - a rule a shadow surface needs, living
 * where only some of them can see it. The three copies happened to be byte-identical when they were
 * gathered here (releases, artists, settings); the next edit to any one of them would have been the
 * fork, and nothing in this repo could have seen it, because each file reads perfectly alone. The
 * owner's convention is promotion into a fragment rather than duplication - this is that.
 *
 * ⚠ The disclosure triangle is the LITERAL ▸ (U+25B8), not a backslash CSS escape: an escape inside
 * a css tagged template is fragile, and the glyph reads the same.
 */
export const accordionStyles = css`
	/* ⚠ **BOTH margins, and the bottom one was missing until 2026-08-09** (owner report: the SMTP card's
	   "Using a Gmail address?" disclosure sat flush against the Server label under it). It had
	   margin-top alone, so an accordion had breathing room above and none below - invisible wherever one
	   is the LAST thing in its container, which is most of the call sites, and obvious the moment a form
	   field follows it. **The symmetric pair is what generalizes**: a rule that only spaces one side is
	   correct exactly until something is placed on the other.
	   ⚠ These are MARGINS rather than a parent gap on purpose, and that is what makes the fix safe where
	   accordions stack: adjacent vertical margins COLLAPSE in normal flow, so two of these in a row still
	   sit 8px apart rather than 16. A caller that puts them in a flex container would get both, since
	   flex suppresses collapsing - so space accordion siblings with flow, not flex. */
	details.accordion {
		border: 1px solid var(--border);
		border-radius: var(--radius);
		background: var(--lift);
		margin-top: 8px;
		margin-bottom: 8px;
	}
	details.accordion > summary {
		cursor: pointer;
		padding: 8px 12px;
		font-size: 0.8125rem;
		color: var(--text2);
		list-style: none;
		display: flex;
		align-items: center;
		gap: 8px;
		user-select: none;
	}
	details.accordion > summary::-webkit-details-marker {
		display: none;
	}
	details.accordion > summary::before {
		content: "▸";
		font-size: 0.6875rem;
		transition: transform var(--motion-fast) var(--motion-ease);
	}
	details.accordion[open] > summary::before {
		transform: rotate(90deg);
	}
	details.accordion > .accordion-body {
		padding: 0 12px 10px;
	}
	/* An edit modal's sections. Heavier than the plain accordion (which can nest INSIDE one), because
	   these are the modal's spine: the summary has to read as a heading rather than as a hint, or a
	   collapsed section looks like a note about the field above it. */
	details.editor-section {
		margin-top: 12px;
	}
	details.editor-section > summary {
		font-size: 0.875rem;
		font-weight: 600;
		color: var(--text);
		padding: 10px 12px;
	}
	details.editor-section[open] > summary {
		border-bottom: 1px solid var(--border);
		margin-bottom: 10px;
	}
	details.editor-section > summary .editor-section-title {
		flex: 1;
	}
	details.editor-section > summary .accordion-count {
		font-weight: 400;
	}
`;

/**
 * The loading SKELETON - the grey shimmer standing in for content that has not arrived.
 *
 * ⚠ **This fragment exists because there were three hand-copied versions of it and the Content page
 * was about to be the fourth** (2026-08-20). `public/style.css` has one for the light pages,
 * `pages/blogs.ts` and `pages/dashboard.ts` each carry a shadow twin - and they had already drifted:
 * the line is 16px tall in the global sheet, 34px in blogs, 20px in the dashboard, with three
 * different bottom margins. None of that was a decision; it is what copying costs. The heights stay
 * settable per call site through the two custom properties below, which is the difference between a
 * shared rule and a rule that forces every skeleton to look the same.
 *
 * ⚠ **The `@keyframes` travels INSIDE the fragment, and that is the whole reason this can be shared
 * at all.** Keyframes are TREE-SCOPED: a rule animating `skeleton-shimmer` finds nothing unless the
 * name is defined in the same root. Composing this fragment carries both halves into whichever shadow
 * root asks for it - which the three existing copies could not do, and is why they were copies.
 *
 * ⚠ **The duration and the iteration count are MOTION TOKENS, never literals.** A hardcoded
 * `1.5s infinite` is a shimmer that `prefers-reduced-motion` cannot reach, and a page-load animation
 * that ignores that preference is the exact case the setting exists for - `--motion-decor-iterations`
 * collapses it to a single pass.
 *
 * ⚠ **A skeleton is `aria-hidden`, at the call site.** It is a picture of absent content; announcing
 * "blank blank blank" to a screen reader is worse than announcing nothing, and the honest thing to
 * announce is the `aria-busy` on the region it stands in.
 */
export const skeletonStyles = css`
	.loading-skeleton {
		padding: 8px 0;
	}
	.skeleton-line {
		height: var(--skeleton-height, 16px);
		background: linear-gradient(90deg, var(--bg3) 25%, var(--border) 50%, var(--bg3) 75%);
		background-size: 200% 100%;
		animation: skeleton-shimmer var(--motion-decor-loop) var(--motion-decor-iterations);
		border-radius: var(--skeleton-radius, 4px);
		margin-bottom: 8px;
	}
	.skeleton-line.short {
		width: 60%;
	}
	@keyframes skeleton-shimmer {
		0% {
			background-position: 200% 0;
		}
		100% {
			background-position: -200% 0;
		}
	}
`;

/**
 * The "?" help trigger beside a label.
 *
 * ⚠ Promoted out of three page islands on 2026-08-04, when the outlet modal was about to become the
 * fourth - and it would have rendered as a bare question mark, because a class that styles nothing
 * looks exactly like a class that styles something until someone looks. Same bug class as
 * accordionStyles above: a rule a shadow surface needs, living where only some of them can see it.
 *
 * ⚠ The tooltip BEHAVIOR is bound per shadow root by GhElement (see tooltip.ts) - a document-level
 * delegated listener never sees a hover that starts and ends inside one tree. This fragment is only
 * the look.
 */
export const tooltipStyles = css`
	.tooltip-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 14px;
		height: 14px;
		border-radius: 50%;
		border: 1px solid var(--border);
		background: var(--bg3);
		color: var(--text2);
		font-size: 0.625rem;
		font-weight: 600;
		cursor: help;
		margin-left: 4px;
		vertical-align: middle;
		flex-shrink: 0;
		/* ⚠ THE NUDGE IS NOT A FUDGE, and the owner's eye found it (2026-08-12: *"seems slightly
		   off"*, about the ? beside an empty state's sentence). The middle keyword above aligns a
		   box's centre with the baseline plus HALF THE X-HEIGHT - the middle of a lowercase x. The
		   ink of a sentence like "No sections yet." runs from the baseline to the CAP height, so its
		   optical centre is half the cap height up, and the badge lands low by half the difference
		   between the two. Measured in Chrome against the app's own font stack: 1.75px at 16px text,
		   which is exactly the "slightly" a reader can see and not name.
		   ⚠ **The em here resolves against the BADGE's own font-size (0.625rem), not the sentence's**,
		   which is the trap that made the first two attempts under-correct by half - a value picked
		   from the surrounding text's size is wrong by the ratio between them. -0.175em of 10px is
		   the 1.75px the measurement asked for, and it re-measured to 0.00px off.
		   ⚠ It is therefore exact for body text and approximate beside a heading, since the badge's
		   size is fixed while the text's is not - but the residual there is under half a pixel,
		   against the 1.75 it replaces. */
		position: relative;
		top: -0.175em;
	}
	/* The same trigger, ON the control it explains (owner, 2026-08-04) - the plan wall's badge
	   shape one lane over, and for the same reason: a mark on a control's corner is read as
	   belonging to that control, while anything placed BESIDE it reads as a second thing in the
	   row. It is what replaced the sentences that used to sit next to buttons.
	   ⚠ The wrapper must be the positioned ancestor, so the badge is a SIBLING of the button
	   rather than a child - a child of a disabled button cannot be hovered at all, which is the
	   trap the plan-wall fragment above documents at length. */
	.tip-wrap {
		position: relative;
		display: inline-flex;
	}
	.tooltip-icon.tip-corner {
		position: absolute;
		top: -7px;
		right: -7px;
		width: 16px;
		height: 16px;
		margin-left: 0;
		/* A ring in the PAGE color so the badge sits ON the button rather than inside its fill,
		   with the border kept as a shadow so the circle still reads at rest. */
		border: 2px solid var(--bg);
		box-shadow: 0 0 0 1px var(--border);
	}
	/* The same badge saying something is PENDING rather than merely explaining (§R's unsaved marker).
	   ⚠ It is an exclamation on --warning rather than the muted question mark, because the two are
	   read differently at a glance and only one of them is a reason to look: a corner mark in the
	   chrome color is furniture, and this one is the difference between a setting that survives a
	   reload and one that does not. Same shape and same rules as the plan wall's flag - a sibling
	   inside .tip-wrap, never a child - so a call site cannot get it wrong in a new way.
	   ⚠ No backticks in this comment, and that is not a style choice: a backtick inside a css tagged
	   template ends the template. It has cost two rounds. */
	.tooltip-icon.tip-attention {
		background: var(--warning);
		color: var(--on-warning);
		border-color: var(--bg);
		cursor: help;
	}
`;

/**
 * THE FLOATING ACTION BAR - a pill that appears at the bottom of the viewport when a selection
 * exists, carrying the count and what can be done with it (owner ask, 2026-08-05, after seeing the
 * pattern on a scratch page: *"it feels very modern and nicely designed"*).
 *
 * ⚠ **It earns its place by fixing a real thing, not by looking modern.** On Compose you tick
 * outlets down a list that scrolls for pages, and the button that acts on them is BELOW the list -
 * so picking forty means scrolling back to find the action, and the only place the count appears is
 * inside the button's own busy label. A bar that follows the viewport puts the count and the action
 * where the eye already is.
 *
 * ⚠ **It renders only while there IS a selection.** A permanent bar is chrome that costs a strip of
 * every screen for a state most visits never enter; one that arrives with the first tick is an
 * answer to something the person just did.
 *
 * ⚠ **`position: fixed` inside a shadow root is fine and one thing breaks it**: an ancestor with a
 * `transform`, `filter` or `backdrop-filter` becomes the containing block, and the bar would then
 * float inside a card instead of the window. If it ever lands in the wrong place, look UP the tree
 * for those three properties rather than at this rule.
 *
 * ⚠ **The entrance is a motion TOKEN, never a literal duration** - `prefers-reduced-motion` flips
 * the tokens at `:root` and cannot reach a hardcoded `0.2s` (round 149, `tests/motion.test.ts`).
 */
export const actionBarStyles = css`
	.action-bar {
		position: fixed;
		/* Centered on the CONTENT column, not the window: the sidebar and the column's cap come in as
		   the shell's tokens (public/style.css), which inherit through every shadow root. */
		left: calc(var(--shell-inset, 0px) + min(100vw - var(--shell-inset, 0px), var(--shell-content-max, 100vw)) / 2);
		bottom: 22px;
		transform: translateX(-50%);
		z-index: 60;
		display: flex;
		/* ⚠ THE PHONE (2026-08-10): a centered pill with no width limit grows past both screen edges,
		   and the half that carries the ACTION is the half that goes off-screen. Clamped and wrapping. */
		flex-wrap: wrap;
		justify-content: center;
		max-width: calc(100vw - 24px);
		align-items: center;
		gap: 6px;
		padding: 7px 9px;
		border-radius: 999px;
		border: 1px solid var(--border);
		/* Translucent over the page rather than opaque: the bar sits ON the list it acts on, and
		   seeing the rows move under it is what says the two are connected. */
		background: color-mix(in srgb, var(--bg2) 88%, transparent);
		backdrop-filter: blur(10px);
		/* The app's own pop shadow, not a new one: both themes already define it, and a literal
		   rgba here would be invisible in the theme it was not written in (tests/theme.test.ts). */
		box-shadow: var(--shadow-pop);
		animation: action-bar-in var(--motion-base) var(--motion-ease) both;
	}
	@keyframes action-bar-in {
		from {
			opacity: 0;
			transform: translate(-50%, 10px);
		}
		to {
			opacity: 1;
			transform: translate(-50%, 0);
		}
	}
	/* The count is a STATE, so it stays inline as a word - the rule that sends sentences behind a "?"
	   is about explanations, and "3 selected" is the reason the bar is on screen at all. */
	.action-bar .count {
		color: var(--text2);
		font-size: 0.8125rem;
		padding: 0 6px 0 8px;
		white-space: nowrap;
	}
	.action-bar .sep {
		width: 1px;
		height: 20px;
		background: var(--border);
		margin: 0 2px;
	}
	.action-bar .btn {
		margin: 0;
	}
`;

/**
 * THE TABLE WRAPPER, and why it grew a SURFACE (2026-08-05).
 *
 * ⚠ **The owner found this by looking at the step-sequencer texture turned on app-wide**: on every
 * page built of cards it read beautifully, because the cards are opaque and the dots show only in
 * the gutters - and on the Outlets page the dots ran straight THROUGH the table, between a name and
 * its URL, behind the header row. Their own diagnosis is the rule: *"it would likely have looked
 * good on the outlets page if the table had a background color too."*
 *
 * So the fix is not a texture exception per page, it is that **a table is content and content sits
 * on a surface**. These pages were the only ones in the app whose main content had nothing under it,
 * which is why they were also the only ones where a page-level background could interfere.
 *
 * ⚠ **Promoted rather than copied.** The three lines below were hand-duplicated in SEVEN page
 * islands, byte-identical, and the round that adds a surface to one of them is not the round that
 * remembers the other six - the exact failure `accordionStyles` and `tooltipStyles` were promoted to
 * stop. A fragment is also what makes "give every table a surface" one edit instead of a sweep.
 *
 * ⚠ **`overflow-x: auto` stays**, and with a radius it needs the clip: a table wider than its wrapper
 * scrolls under the rounded corner, and without `overflow: hidden` on the box the first column's
 * cells square it off again on scroll.
 */
export const tableStyles = css`
	.table-wrap {
		margin-top: 16px;
		overflow-x: auto;
	}
	/* ⚠ **The surface is conditional on there actually BEING a table, and the owner found out why.**
	   Two pages wear this class over a list of CARDS rather than a table - the artists roster and the
	   releases list - so a blanket surface drew a second card around a card, with the wrapper's empty
	   right-hand side stretching across the page as dead outlined space. The :has() selector keys the rule on the
	   same thing the rule is FOR, which is the repo's own "derive it from the thing that cannot lag":
	   a wrapper that starts holding a table gets the surface with no edit, and one that stops holding
	   one loses it.
	   ⚠ It also does the right thing for an EMPTY list: no table, no surface, so an empty state sits
	   on the page's own field with the texture behind it - which is the one place a texture has room. */
	.table-wrap:has(table) {
		background: var(--bg2);
		border: 1px solid var(--border);
		border-radius: var(--radius);
	}
	/* The table fills the surface, so its own outer border would be a second line on the same edge. */
	.table-wrap table {
		margin: 0;
	}
	.table-wrap tr:last-child td {
		border-bottom: 0;
	}
`;

/**
 * THE ACTIONS ROW at the end of a table row, and both of its rules are arithmetic rather than taste.
 *
 * ⚠ **Promoted on 2026-08-13 because there were SEVEN hand-copies of it** - `.table-actions` in
 * artists, sent, accepted, settings, releases and blogs, and `.blog-actions` in blogs and discover -
 * and the seventh had already drifted exactly the way `tableStyles`' own note predicts: discover's
 * twin never received the `flex-wrap: wrap` that 2026-08-10's phone round added to the other six, so
 * its row still overflows its cell on a narrow screen. A rule that seven roots copy is a rule that
 * gets fixed six times.
 *
 * ⚠ **`min-width: max-content` is what keeps a button INSIDE its own cell** (owner, 2026-08-13, in
 * saas: *"'Switch off' exceeds the table dimensions on the Outlets page"* - measured at 6px past the
 * cell's edge and 18px past its content box). The chain is worth keeping, because every link in it
 * is correct on its own:
 *
 * - A `.btn` is a WRAPPING flex row (`flex-wrap: wrap` in `buttonStyles`, deliberately - a long label
 *   breaks inside the button instead of overflowing it).
 * - **A multi-line flex container's min-content width is its WIDEST ITEM**, not the sum of its items:
 *   the label alone, with the icon and the gap left out, because a wrapping box can always put them
 *   on the next line. Here that is 79px against a rendered 97px.
 * - The outlets table's actions cell asks for exactly that measurement - `width: 1%` is the
 *   shrink-to-fit idiom for an auto table - so the column is built 18px too narrow.
 * - And then `white-space: nowrap` on the row makes the wrap the measurement assumed IMPOSSIBLE, so
 *   the button draws at its full width and hangs out of the cell it was measured into.
 *
 * ⚠ **So the generalizable rule is: a button that cannot wrap must be MEASURED as one that cannot.**
 * Nothing about this is specific to a table - any container sized by min-content (a grid `auto`
 * track, a `fit-content` box, a flex item at its automatic minimum) under-measures a nowrap icon
 * button by the icon plus the gap, and the failure looks like the container being wrong rather than
 * the button. The 2026-08-11 round read the same symptom as an auto table handing the last column
 * whatever the prose columns left, added the `width: 1%`, and fixed the visible half: the column now
 * shrinks to fit, and what it fits was still the wrong number.
 */
export const tableActionsStyles = css`
	.table-actions,
	.blog-actions {
		display: flex;
		/* ⚠ THE PHONE (2026-08-10): nowrap plus a no-shrink button is arithmetic, not a preference -
		   the row overflows its cell at any width narrower than its buttons. The nowrap stays (it
		   keeps a LABEL on one line); wrapping is what the ROW needed. */
		flex-wrap: wrap;
		gap: 6px;
		align-items: center;
		white-space: nowrap;
	}
	.table-actions .btn,
	.blog-actions .btn {
		/* A squashed label is worse than a wide column, so the button does not give width back... */
		flex-shrink: 0;
		/* ...and this is the same statement made to whatever is SIZING the column. A button wraps
		   internally, so it reports the width of its longest line as its minimum - and the nowrap
		   above means it will never take that line break. See the note above this fragment. */
		min-width: max-content;
	}
`;

/**
 * The ink header band a workspace opens with - the release's (the approved §AL mockup) and, since
 * round 327, the artist's. See the first rule's note for why it is a fragment.
 */
export const bandStyles = css`
	/* THE HEADER BAND (§AL, the approved release mockup): ink, the dots at its top like every dark
	   surface in the redesign, the cover large, the title in the cover's two colors. Drawn in the
	   sidebar's chrome tokens so it follows all four palettes. The song colors default to the app's
	   accent ramp here; the element's inline style carries the cover's once they arrive.
	   ⚠ A FRAGMENT since round 327, when the artist workspace became its second host: the release
	   page carried it as rules on its own header id, and a second hand copy in another shadow root
	   is how two headers drift. A photo is a round cover (band-cover round). */
	.ink-band {
		--song: var(--accent);
		--song-hi: var(--accent-2);
		position: relative;
		overflow: hidden;
		display: flex;
		gap: 28px;
		margin-bottom: 18px;
		padding: 24px 28px;
		border-radius: var(--radius);
		background: var(--chrome-bg);
		color: var(--chrome-text);
	}
	.ink-band::before {
		content: "";
		position: absolute;
		inset: 0 0 auto;
		height: 200px;
		pointer-events: none;
		background-image: radial-gradient(circle, color-mix(in srgb, var(--chrome-text) 20%, transparent) 1px, transparent 1.6px);
		background-size: 20px 20px;
		/* A mask reads only alpha, so any opaque token will do - never a literal (the theme gate). */
		-webkit-mask-image: linear-gradient(to bottom, var(--chrome-bg), transparent);
		mask-image: linear-gradient(to bottom, var(--chrome-bg), transparent);
	}
	.band-cover {
		position: relative;
		flex: none;
		width: 168px;
		height: 168px;
	}
	.band-cover img,
	.band-cover-empty {
		display: grid;
		place-items: center;
		width: 100%;
		height: 100%;
		border-radius: 4px;
		object-fit: cover;
	}
	.band-cover-empty {
		background: color-mix(in srgb, var(--chrome-text) 10%, transparent);
		color: var(--chrome-text2);
	}
	.band-main {
		position: relative;
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		gap: 16px;
	}
	.band-top {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		gap: 16px 24px;
		flex-wrap: wrap;
	}
	.band-titles {
		min-width: 0;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 6px;
	}
	.band-back {
		padding: 0;
		border: 0;
		background: none;
		cursor: pointer;
		font-family: var(--font-mono);
		font-size: 0.75rem;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: var(--chrome-text2);
	}
	.band-back:hover {
		color: var(--chrome-text);
	}
	.band-kicker {
		display: flex;
		flex-wrap: wrap;
		gap: 0 8px;
		margin: 0;
		font-family: var(--font-mono);
		font-size: 0.75rem;
		letter-spacing: 0.05em;
		text-transform: uppercase;
		color: var(--chrome-accent-text);
	}
	.band-kicker span + span::before {
		content: "\\00b7";
		margin-right: 8px;
		color: var(--chrome-text2);
	}
	/* The title wears the cover's two colors. Smaller than the mockup's 64px: the owner found the
	   first pass's headlines too bold and too large. */
	.band-title {
		margin: 0;
		max-width: 100%;
		font-family: var(--font-display);
		font-stretch: var(--font-display-stretch);
		font-weight: 800;
		font-size: 2.75rem;
		line-height: 1;
		overflow-wrap: anywhere;
		background-image: linear-gradient(100deg, var(--song), var(--song-hi));
		-webkit-background-clip: text;
		background-clip: text;
		color: transparent;
	}
	.band-sub {
		margin: 0;
		font-size: 0.9375rem;
		color: var(--chrome-text2);
	}
	.band-actions {
		flex: none;
		display: flex;
		align-items: center;
		gap: 10px;
	}
	/* The shared outline button is drawn for paper; on ink it takes the chrome tokens. */
	.band-edit {
		color: var(--chrome-text);
		border-color: var(--chrome-border-strong);
		background: transparent;
	}
	.band-edit:hover {
		background: color-mix(in srgb, var(--chrome-text) 10%, transparent);
	}
	.band-cover.round img,
	.band-cover.round .band-cover-empty {
		border-radius: 50%;
	}
	@media (max-width: 700px) {
		.ink-band {
			flex-direction: column;
			gap: 16px;
			padding: 18px 16px;
		}
		.band-cover {
			width: 96px;
			height: 96px;
		}
		.band-title {
			font-size: 2rem;
		}
	}
`;

/**
 * THE PAGE STUDIO (round 331) - the Press Kit, Smart Link and Fan Kit tabs, which are one kind of
 * thing and until this round were laid out three ways: two with a row of buttons and one with a
 * "Live at" sentence, one analytics heading at a different size, a Save button in the middle of the
 * smart link's card (it saved the toggles UNDER it too, which nothing on screen said), and the
 * preview at the bottom of the page, a screen away from the controls that change it.
 *
 * Now each is the same four things in the same order: the page's actions on one bar, the preview
 * with the settings beside it (an INSPECTOR, the shape every page builder uses, so a change lands
 * next to the control that made it), and the numbers under both. The three page cards above carry
 * the thirty-day visits and plays, so the lead numbers are still the first thing on the tab.
 *
 * ⚠ **The studio is TWO COLUMNS only when its own box can hold both**, a container query and not a
 * media query: the same tab renders inside the release workspace, whose subnav takes 170px beside
 * it, and a viewport width says nothing about how much of it the subpage got.
 */
export const pageStudioStyles = css`
	.page-bar {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px;
		margin-bottom: 16px;
	}
	.page-bar-meta {
		margin-left: auto;
		font-size: 0.8125rem;
		color: var(--text2);
	}
	/* The live address as one field (round 342, frontend/pageAddress.ts): a dot, the address in mono,
	   Copy and Open as quiet text inside it. The field grows, so the bar's buttons stay their size. */
	.page-live {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-size: 0.8125rem;
		color: var(--success);
	}
	.page-live-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: var(--success);
	}
	.page-address {
		flex: 1 1 260px;
		min-width: 0;
		display: flex;
		align-items: center;
		gap: 12px;
		min-height: var(--control-h);
		padding: 0 12px;
		border: 1px solid var(--border);
		border-radius: var(--radius);
		background: var(--bg2);
	}
	.page-address-url {
		flex: 1;
		min-width: 0;
		font-family: var(--font-mono);
		font-size: 0.8125rem;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.page-address .btn-text {
		text-decoration: none;
		white-space: nowrap;
	}
	.page-studio {
		container-type: inline-size;
		margin-bottom: 28px;
	}
	.page-studio-grid {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 300px;
		gap: 16px;
		align-items: start;
	}
	@container (max-width: 760px) {
		.page-studio-grid {
			grid-template-columns: minmax(0, 1fr);
		}
		.page-inspector {
			position: static;
		}
	}
	/* ⚠ A LONG INSPECTOR swaps which column holds still (round 337, the link-in-bio tab): its settings
	   include a list of every row the page draws - socials, links, music, press - which is taller than
	   a screen, so the inspector scrolls and the PREVIEW stays in view beside it, and the column is
	   wide enough for a row's label, its line of detail and its two arrows. */
	.page-studio-grid.long-inspector {
		grid-template-columns: minmax(0, 1fr) 380px;
	}
	.page-studio-grid.long-inspector > gh-page-frame {
		position: sticky;
		top: 16px;
	}
	.page-studio-grid.long-inspector > .page-inspector {
		position: static;
	}
	@container (max-width: 860px) {
		.page-studio-grid.long-inspector {
			grid-template-columns: minmax(0, 1fr);
		}
		.page-studio-grid.long-inspector > gh-page-frame {
			position: static;
		}
	}
	.page-inspector {
		position: sticky;
		top: 16px;
		display: flex;
		flex-direction: column;
		gap: 16px;
		padding: 16px;
		background: var(--bg2);
		border: 1px solid var(--border);
		border-radius: var(--radius);
	}
	.inspector-section {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
	.inspector-section + .inspector-section {
		padding-top: 16px;
		border-top: 1px solid var(--border);
	}
	.inspector-head {
		display: flex;
		align-items: center;
		gap: 6px;
		margin: 0;
		font-family: var(--font-mono);
		font-size: 0.75rem;
		font-weight: 400;
		letter-spacing: 0.05em;
		text-transform: uppercase;
		color: var(--text2);
	}
	.inspector-field {
		display: flex;
		flex-direction: column;
		gap: 6px;
		min-width: 0;
	}
	.inspector-label {
		display: flex;
		align-items: center;
		gap: 6px;
		margin: 0;
		font-size: 0.8125rem;
		font-weight: 600;
		color: var(--text);
	}
	.inspector-line {
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 0;
	}
	.inspector-line > gh-select,
	.inspector-line > gh-font-select {
		flex: 1;
		min-width: 0;
	}
	.inspector-field input[type="color"] {
		flex: none;
		width: 44px;
		height: var(--control-h);
		padding: 2px;
		border: 1px solid var(--border);
		border-radius: 6px;
		background: var(--bg3);
		cursor: pointer;
	}
	/* The swatch only means something in "pick" mode. Drawn at full strength under "Auto" or "No
	   accent color", it read as the color the page would wear - the Clips tab's .reel-type rule. */
	.inspector-field input[type="color"]:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}
	/* A switch and its ? on one line; a live fact about the release takes the line under it */
	.inspector-toggle {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 4px 6px;
	}
	.inspector-toggle .hint {
		flex-basis: 100%;
		margin: 0;
	}
	.inspector-hint {
		margin: 0;
		font-size: 0.8125rem;
		line-height: 1.5;
		color: var(--text2);
	}
	.inspector-foot .btn {
		width: 100%;
		justify-content: center;
	}
	/* A foot button carrying its ? badge sits in a tip wrapper, which would otherwise shrink it */
	.inspector-foot > .tip-wrap {
		display: flex;
		width: 100%;
	}
	/* The numbers under the studio: gh-stats-panel is inline by default, where a margin is ignored */
	.page-numbers {
		display: block;
		margin-bottom: 26px;
	}
`;

/**
 * THE LOG AS ROWS - the list every record page draws (§AL, 2026-09-23).
 *
 * Sent was a ten-column table and Accepted, Declined and Discover were five- and six-column ones, each
 * carrying its own copy of the table chrome. They are one shape now: who on the first line, what under
 * it, a slot of small signals, the date in a fixed column, the actions at the end. Outlets keeps its
 * own ROW (a checkbox, a type drawing and four numbers) but uses the same tools line and container.
 *
 * ⚠ **`container-type: inline-size` on `.list-rows` is load-bearing twice.** The rows answer to the
 * LIST's width, not the window's (a sidebar and a page width are between the two), and the containment
 * is what stops one long nowrap subject from widening the whole page - round 320 shipped without it
 * on the desktop column and the owner's real data scrolled sideways.
 *
 * ⚠ **The main block's padding is the row's**, not the item's: on Sent it is the button that opens the
 * email, and a button padded inside a padded row would leave a dead band around it that clicks nothing.
 */
export const listStyles = css`
	/* ⚠ The search box needs AIR under it (owner, 2026-08-21, on Sent) - 18px, so it reads as a control
	   and not as the list's first row. The sort picker shares its line: the order used to live in the
	   column headers. */
	.list-tools {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		align-items: center;
		margin-bottom: 18px;
	}
	.list-search {
		flex: 1 1 260px;
		min-width: 0;
	}
	.list-sort {
		flex: 1 0 200px;
		max-width: 260px;
	}
	.list-month {
		margin: 22px 0 8px;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		font-weight: 600;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--text2);
	}
	.list-month:first-of-type {
		margin-top: 0;
	}
	.list-rows {
		container-type: inline-size;
		list-style: none;
		margin: 0;
		padding: 0;
		border: 1px solid var(--border);
		border-radius: var(--radius);
		background: var(--bg2);
		overflow: hidden;
	}
	.list-item {
		display: flex;
		align-items: center;
		gap: 14px;
		padding-right: 12px;
		border-bottom: 1px solid var(--border);
	}
	.list-item:last-child {
		border-bottom: 0;
	}
	.list-item:hover {
		background: var(--bg3);
	}
	.list-main {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 3px;
		padding: 11px 0 11px 16px;
	}
	button.list-main {
		border: 0;
		background: none;
		text-align: left;
		font: inherit;
		color: var(--text);
		cursor: pointer;
	}
	/* The drawing that leads a row of outlets: its type, on a tile (outletTypeTile) */
	.list-tile {
		flex: none;
		display: grid;
		place-items: center;
		width: 36px;
		height: 36px;
		border-radius: 10px;
		background: color-mix(in srgb, var(--accent) 12%, transparent);
		color: var(--accent-text);
	}
	.list-item > .list-tile:first-child {
		margin-left: 16px;
	}
	.list-item > .list-tile + .list-main {
		padding-left: 0;
	}
	.list-name,
	.list-sub {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
	.list-name {
		font-weight: 600;
		font-size: 0.9375rem;
	}
	.list-name small {
		font-weight: 400;
		font-size: 0.8125rem;
		color: var(--text2);
	}
	.list-sub {
		font-size: 0.8125rem;
		color: var(--text2);
	}
	.list-sub b {
		font-weight: 600;
		color: var(--text);
	}
	/* A note or a reply the row quotes: two lines, then an ellipsis */
	.list-note {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		font-size: 0.8125rem;
		color: var(--text2);
	}
	.list-signals {
		flex: none;
		display: flex;
		align-items: center;
		gap: 12px;
	}
	.list-date {
		flex: none;
		min-width: 6.5em;
		text-align: right;
		font-family: var(--font-mono);
		font-size: 0.75rem;
		font-variant-numeric: tabular-nums;
		color: var(--text2);
		white-space: nowrap;
	}
	.list-actions {
		flex: none;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 6px;
	}
	/* A narrow list: who and what take the whole first line, the rest wraps under them */
	@container (max-width: 720px) {
		.list-item {
			flex-wrap: wrap;
			gap: 8px 12px;
			padding: 0 12px 10px 0;
		}
		.list-main {
			flex-basis: 100%;
			padding-bottom: 0;
		}
		.list-tile + .list-main {
			flex-basis: calc(100% - 64px);
		}
		/* What wraps starts under the row's TEXT, never flush against the box: 16px is the main
		   block's own padding, and past a tile it is the tile's margin, width and gap (16 + 36 + 12).
		   Discover carried the actions half of this locally, and Templates met the flush version. */
		.list-signals,
		.list-actions {
			padding-left: 16px;
		}
		.list-tile ~ .list-signals,
		.list-tile ~ .list-actions {
			padding-left: 64px;
		}
		.list-date {
			min-width: 0;
			margin-left: auto;
		}
	}
	@media (max-width: 720px) {
		.list-sort {
			max-width: none;
		}
	}
`;

/**
 * The Quick start step card - the picture, the gradient heading, and the room the picture needs.
 *
 * ⚠ **It moved here on 2026-08-07 because the block now has THREE hosts**: the guide itself, the
 * Releases page's empty state and the Artists page's (owner ask - *"the 'Releases' empty state should
 * be replaced with the 'Your first release' from the Quick Start guide, including with the
 * illustration to the right of it"*). Copying it would have made three copies of the two hardest
 * rules in it, which are hard precisely because they were each got wrong once and fixed on a report:
 * the 150px text reservation, and the viewBox that crops to the drawing's INK.
 *
 * ⚠ The class prefix is `start-`, not `onb-`. These are no longer onboarding's - a class named after
 * the first surface that used it is `landingShowcase.ts`'s finding one lane over (§Z), and the whole
 * point of the move is that the second and third callers are not that page.
 */
export const startCardStyles = css`
	/*
	 * ⚠ **NO overflow: hidden here, and the day it was removed is worth writing down** (owner report,
	 * 2026-08-18: the quick start's Genre dropdown *"renders inside the card it is contained within,
	 * and it is so narrow it's impossible to read what's in it"*). The clip was doing nothing this
	 * card needs - a background gradient is clipped by the border radius on its own, and the
	 * illustration is positioned inside the padding box - and it was silently amputating the one kind
	 * of child that is SUPPOSED to leave a card: a dropdown panel.
	 *
	 * ⚠ **The general rule, because this will happen again: an overflow clip is a promise that no
	 * descendant will ever need to escape**, and a card that hosts a form cannot make that promise.
	 * Every popover in this app (the select, the multiselect, the wrap bubble) is an absolutely
	 * positioned child, so ANY ancestor with an overflow of hidden or auto between it and the viewport
	 * clips it - which reads as the control being broken rather than as a container being strict. It
	 * is the sibling of the fixed-position trap the action bar's note carries: **when a floating
	 * thing lands wrong, look UP the tree, never at the thing.**
	 */
	.start-step {
		position: relative;
		background-image: linear-gradient(135deg, color-mix(in srgb, var(--accent) 9%, transparent) 0%, transparent 46%);
	}
	/* The done card trades the accent wash for a green one - the same shape saying a different
	   thing, so a finished step is recognisable from across the page rather than by its tick. */
	.start-step.start-done {
		background-image: linear-gradient(135deg, color-mix(in srgb, var(--success) 9%, transparent) 0%, transparent 46%);
	}
	.start-head {
		display: flex;
		align-items: center;
		gap: 10px;
	}
	/* The title carries the wordmark's own gradient (owner ask, 2026-08-07). The ramp STARTS in the
	   text color and only reaches the accent at the tail, so the words read as text with a highlight
	   instead of as tinted decoration.
	   NOTE - background-clip:text needs a transparent fill, and a transparent fill is invisible if the
	   gradient ever fails to paint. The plain color above it is that fallback. */
	.start-head h3 {
		margin: 0;
		color: var(--text);
		background-image: linear-gradient(100deg, var(--text) 0%, var(--accent-text) 72%, var(--accent-2) 100%);
		/* NOTE - NOT the wordmark's 220%. That value gives its sheen animation somewhere to travel, and
		   copying it here put the accent stops off the right-hand end of a two-word heading. A borrowed
		   rule has to be read for WHY each value is what it is: the ramp transfers, the travel does not. */
		background-size: 100% 100%;
		-webkit-background-clip: text;
		background-clip: text;
		/* ⚠ High contrast turns it OFF (section R). That palette exists so nothing competes with the
		   words, and a heading painted in a gradient is the definition of something competing.
		   ⚠ Through a TOKEN (round 305): this used to be :host-context([data-contrast="more"]), and
		   the app only ever stamps "high" or "standard", so the rule never matched in any browser - and
		   :host-context does not exist in Firefox at all. --ramp-fill is transparent in the
		   standard palettes and the text color in the high-contrast ones, and a solid fill paints over
		   the clipped ramp, so one inherited value switches the whole effect. */
		-webkit-text-fill-color: var(--ramp-fill);
	}
	/* The illustration. Absolutely positioned and non-interactive, so it can never take a click meant
	   for the form under it and never pushes the layout around - decoration that moves a button is not
	   decoration.
	   ⚠ Flush with the card's own 20px padding, so the drawing's right edge lines up with the controls
	   below it (owner report, twice). NOTE - the first fix set this correctly and the drawing STILL
	   looked inset: every illustration composes its sheet at x=14 inside a 132-wide viewBox, so 14
	   units are empty on each side. The element was aligned and the picture was not. The component
	   crops the viewBox to the sheet. Aligning a drawing means aligning its INK. */
	.start-art {
		position: absolute;
		top: 12px;
		right: 20px;
		width: 104px;
		height: 76px;
		pointer-events: none;
		opacity: 0.55;
	}
	/* ⚠ **The card is never shorter than its own drawing** (owner report, 2026-08-07: the illustration
	   bled into the card below it). The horizontal reservation above protects TEXT, and that was the
	   whole story while the only host was the guide - where the head and the hint wrap to several
	   lines in a 760px column and the picture is comfortably inside them. On the Artists page the card
	   is the full page width, so both wrap to ONE line each and the block underneath - a full-width
	   finder box, not text, so no padding rule reaches it - started higher than the drawing ended.
	   ⚠ The general shape: a decoration taken out of flow is only safe while something else guarantees
	   the height it needs, and moving the component to a second surface is exactly what removes that
	   guarantee. The floor states the requirement instead of relying on the text to imply it. */
	.start-step:has(.start-art) {
		min-height: 112px;
	}
	/* ⚠ The text RESERVES room rather than flowing under the drawing, and the first attempt got this
	   wrong in a way worth keeping: it hid the drawing below an 860px VIEWPORT, which never fires,
	   because the column is capped at 760px and the card is that width on a 4K monitor too. A media
	   query on the viewport cannot protect a fixed-width column - what decides the overlap is the CARD. */
	.start-head,
	.start-body > .hint:first-child {
		padding-right: 150px;
	}
	/* Below this the card really does shrink with the viewport, and there is no width left to reserve.
	   A picture behind a form field is worse than no picture. */
	@media (max-width: 700px) {
		.start-art {
			display: none;
		}
		.start-head,
		.start-body > .hint:first-child {
			padding-right: 0;
		}
	}
	/* The step number: a quiet circle that flips to a tick once the DB says the step happened.
	   ⚠ Rendered ONLY on the guide (owner, 2026-08-07: *"only show the step badge (1, 2, 3) on the
	   Quick start page"*), and the reason generalizes: a number means "step N of a sequence", and on
	   an empty Releases page there is no sequence for it to be step N of - it would be answering a
	   question nobody on that screen asked. The component takes the step number as an optional
	   property and draws nothing when it is absent. */
	.start-mark {
		width: 26px;
		height: 26px;
		border-radius: 50%;
		background: var(--bg3);
		border: 1px solid var(--border);
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 auto;
		font-size: 0.85rem;
		color: var(--text2);
	}
	.start-step.start-done .start-mark {
		background: color-mix(in srgb, var(--success) 18%, transparent);
		border-color: var(--success);
		color: var(--success);
	}
	.start-body {
		margin-top: 12px;
	}
`;

/**
 * The artwork slot - `frontend/imageSlot.ts` draws it and carries the reasoning. Composed by every
 * island with a picture field (the release editor's three, the artist editor's avatar), because a
 * shadow root gets no rule it was not given.
 */
export const imageSlotStyles = css`
	/* The artwork slot drawn by frontend/imageSlot.ts, which carries the WHY. The tile
	   is a button, so it needs the button reset first: a UA button brings its own border, its own
	   background and a centred inline font, none of which belong on a picture. */
	.image-slot {
		display: flex;
		align-items: center;
		gap: 12px;
	}
	.image-tile {
		position: relative;
		overflow: hidden;
		padding: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 1px dashed var(--border-strong);
		border-radius: 8px;
		background: var(--bg);
		color: var(--text2);
		cursor: pointer;
		transition:
			border-color var(--motion-fast) var(--motion-ease),
			background var(--motion-fast) var(--motion-ease);
	}
	.image-tile.image-filled {
		border-style: solid;
		border-color: var(--border);
	}
	.image-tile:hover,
	.image-tile.image-drop {
		border-color: var(--accent);
		background: color-mix(in srgb, var(--accent) 8%, transparent);
	}
	.image-tile img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
	.image-empty {
		font-size: 1.5rem;
		color: var(--text2);
		display: inline-flex;
	}
	/* The veil says what a click on a FILLED tile does. It is a second telling of what the button
	   beside it already says - never the only one - so hover-only is fine here, and it is also
	   revealed on keyboard focus so the two paths agree. */
	.image-veil {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 4px;
		text-align: center;
		font-size: 0.6875rem;
		font-weight: 600;
		color: var(--text);
		background: color-mix(in srgb, var(--bg) 78%, transparent);
		opacity: 0;
		transition: opacity var(--motion-fast) var(--motion-ease);
	}
	.image-tile:hover .image-veil,
	.image-tile:focus-visible .image-veil {
		opacity: 1;
	}
	.image-actions {
		display: flex;
		flex-direction: column;
		gap: 6px;
		align-items: flex-start;
	}
	.image-buttons {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}
	/* A person is round, a cover is not - the caller says which */
	.image-tile.image-round,
	.image-tile.image-round img {
		border-radius: 999px;
	}
`;
